import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import Loader from "../child/Loder";
import { handleUnauthorized } from "../../utils/apiHelper";
import { getModulePermissions } from "../../utils/permissionUtils";
import { Icon } from "@iconify/react";
import PocketsCard from "./PocketsCard";
import DoorsDashboardCard from "./DoorsDashboardCard";
import SettingsSection from "./SettingsSection";
import SaveBar from "./SaveBar";
import FlipPreviewPanel from "./FlipPreviewPanel";
import { getFlipPreviewFlags, runFlipPreview } from "../services/flipPreviewService";
import FlagRow from "./FlagRow";
import HelpButton from "./HelpButton";
import EventbriteCard, { useEventbriteConnection } from "./EventbriteCard";
import { integrationsSummary } from "./eventbriteUtils";
import { SECTIONS, DEFAULT_OPEN, summarise, REACHES_MEMBERS, etInputOf, instantOfEtInput } from "./settingsMeta";

// Density rollout feature flags (order = rollout order). All default OFF;
// the server enforces every flag — these switches are the operator control.
const FLAG_DEFS = [
  {
    name: "pocket_gating",
    label: "Pocket Gating",
    help: "Compatibility introductions are restricted to members of the same active launch pocket. OFF = introductions work exactly as before pockets existed.",
  },
  {
    name: "low_density_ux",
    label: "Waitlist Experience",
    help: "Waitlisted/out-of-area users see the waitlist home instead of empty screens. (Ships with Part 2.)",
  },
  {
    name: "cohort_drops",
    label: "Cohort Drops",
    help: "Referral codes and pocket-activation machinery. (Ships with Part 3.)",
  },
  {
    name: "events_density",
    label: "Events",
    help: "Events discovery and event-driven waitlist boosts. (Ships with Part 4.)",
  },
  {
    name: "penpal_beyond_city",
    label: "Beyond Your City",
    help: "Pen-pal option for users with no introductions after 48h. Keep OFF until the moderation runbook has handled real reports.",
  },
];

// Engagement flags — not part of the city rollout. Each has an optional
// "Club early access" companion that turns the feature on for Curiouzz
// Club members only while the main switch is still OFF.
const ENGAGEMENT_FLAG_DEFS = [
  {
    name: "daily_question",
    label: "Daily Question",
    help: "One shared question per pocket each day, with an evening reminder push. Keep OFF until the app version that shows the question card is live on the stores. Turning it on pushes it to real users immediately.",
  },
  {
    name: "daily_question_club_early",
    label: "Daily Question, Club early access",
    help: "Gives Daily Question to Curiouzz Club members only, while everyone else still waits. Ignored once the main Daily Question switch above is ON (then everyone has it).",
  },
  {
    name: "meetup_v1",
    label: "Plan a Meetup",
    help: "Local restaurant and cafe suggestions in post-reveal chats, with link-out booking. Free for all revealed pairs. Keep OFF until the app version with the meetup chip (v1.8) is live on the stores.",
  },
  {
    name: "intro_card_v1",
    label: "Introduction Card",
    help: "The v1.8 Compatibility stage: the presence field with the introduction's Curiouzz Side sheet, the Vault journey and the reveal handoff. Shows to anyone with a live pair. Turning it OFF restores the classic Compatibility screen for everyone instantly.",
  },
  // Take a break (founder go 2026-09-27). The switch opens only the member's
  // own door in 2.4; the server honours a pause whatever it says, which is
  // how support pauses 2.3 members from the member page today.
  {
    name: "intro_pause_v1",
    label: "Pause Introductions in the App",
    help: "Shows the 'Introductions: On / Paused' row and the pause sheet in the 2.4 app. Turn ON once 2.4 is live on both stores. The pause itself works whatever this switch says: support can pause a member on their written request from the member page.",
  },
  // Kept, Together (founder go 2026-10-02, backend utils/keptTogether).
  // Declared here, in the backend schema and in the public app config list,
  // or the next save of this page turns it off.
  {
    name: "kept_together_v1",
    label: "Kept, Together",
    help: "At the conversation limit, a pair whose phones both run 2.4 or later gets a free sealed card with three questions instead of the Club wall. Once both have answered, that conversation has no count and no daily limit. OFF stops new cards only: cards already given keep working and opened conversations stay open. Pairs with a phone older than 2.4 keep today's limit and Club wall.",
  },
  // The Club value round (founder go 2026-10-07). Declared here, in the
  // backend schema and in the public app config list.
  {
    name: "read_in_club_v1",
    label: "The Read, included with the Club",
    help: "Every Curiouzz Club member can open their Curiouzz Read while their Club is live, and the 2.5 Club screen says so. A Read bought on its own is unchanged. OFF = the Read is sold on its own only.",
  },
  {
    name: "daily_question_together_v1",
    label: "Daily Question, together",
    help: "Once two people who have revealed with each other have both answered the day's question, and either has the Club, both answers appear in their conversation on 2.5 phones. One member's Club lights it for both. OFF serves nothing.",
  },
  {
    name: "daily_question_ask_v1",
    label: "Ask the Daily Question",
    help: "Club members on 2.5 can send one question with two to four answers for their city. It appears in Vault Questions as Pending review and runs only once you publish it; the card credits a Club member, never a name. OFF hides the row.",
  },
  {
    name: "daily_question_together_stays_v1",
    label: "Daily Question, together: the card stays",
    help: "By default the two answers leave a conversation once either person has sent a message after them. ON keeps them for their full two days instead, whatever gets said. Takes effect on the next open of the conversation; no app build needed.",
  },
  // "Change my photos" after a failed face check on the way in (founder
  // 2026-10-13, "2. Yes"). Declared here, in the backend schema and in the
  // public app config list, or the next save of this page turns it off.
  {
    name: "face_check_change_photos_v1",
    label: "Change My Photos After a Failed Face Check",
    help: "During sign-up, when the face check says the selfie did not look enough like the member's photos, the 2.5 app shows 'Change my photos' beside Try Again. It opens the photo step over the face check with their saved photos: they can replace, remove or reorder them, with the same rules as sign-up (at least 2, at most 4, and the same checks on every new photo). Continue saves them and goes back to the face check, which compares a new selfie with the photos just saved. Never shown on a re-verify from the profile, nor after a failure that is about the light or on our side. While ON, the server also watches: when a check passes after a failed check and a photo change, and one of the photos looks nothing like the selfie (under 50, the bar is 90), the admin bell says so, so you can look at that account's photos. Review only: nothing changes on the account. OFF = Try Again only, as before, and no watching; older apps never show it.",
  },
  // The 2.6 onboarding in four chapters (2.6 build plan S2, 2026-10-15).
  // Declared here, in the backend schema and in the public app config list,
  // or the next save of this page turns it off. Who tests it while it is OFF
  // is backend utils/flagTesters (ADDRESS_TESTERS).
  {
    name: "onboarding_v26",
    label: "New Onboarding in Four Chapters",
    help: "On phones running 2.6 or newer, someone signing up gets the new onboarding: four chapters of cards (About you, Open to, Your world, Real you) in place of one question per screen. Both save the same steps, so someone part-way through when this changes goes on in the other one at their first step not yet done. 2.5 and older never read this switch and keep today's screens. Testers see it while this is OFF: on a 2.6 build with the new Create account screen (today's app removes the + as you type), sign up with an address on our own domain that has +onb after the name, for example developer+onb1@curiouzz.com (the code arrives in developer@curiouzz.com). Use a new address for each run, such as +onb2 or +onb3. Only an email sign-up counts, never Google or Apple. Every account made that way is a test account, only ever introduced to other test accounts. Real members and every other account, Sam, Alex and the App Review accounts included, keep today's onboarding until this is ON. To stop one tester seeing it, delete that account. OFF = today's onboarding for everyone but those testers.",
  },
  // The city map on the 2.6 place card (founder 2026-10-16 "City map, in our
  // colours", 2026-10-19 "Go with Google"; backend utils/placeMap). Declared
  // here, in the backend schema and in the public app config list. Unlike the
  // rest, ON until stored as false (backend DEFAULT_ON_APP_FLAG_NAMES): the
  // server reports it ON while unset and saves an OFF as sent. A plain switch,
  // so not in IMMEDIATE_FLAGS or CONFIRM_OFF_FLAGS: OFF only brings back the
  // grid the card already has.
  {
    name: "place_map_v1",
    label: "City Map on the 'Where Are You Based?' Card",
    help: "On phones running 2.6 or newer, the 'Where are you based?' card of the new onboarding shows the member's city on a real Google map in our colours once the city is known: centred on the city, never on the member's own spot, with our gold pin in the middle. Picking another city under 'Not right?' swaps the map. While the city is being found, and whenever no map comes back, the card shows the drawn grid. The server fetches each map from Google, so our key never reaches a phone, and keeps it in memory for 24 hours at most. It asks Google at most 300 times a day in all and 20 times a day for one member, well under the free allowance; past either, the card keeps the grid. ON unless turned OFF here. 2.5 and older never read this switch. OFF = the drawn grid for everyone, and the server asks Google nothing.",
  },
  // Create account and Sign in with an emailed code (founder go 2026-10-19,
  // "Yes, build it into 2.6"; backend controllers/api.emailCodeController).
  // Declared here, in the backend schema and in the public app config list.
  // ON until stored as false, like place_map_v1 (backend
  // DEFAULT_ON_APP_FLAG_NAMES): the server reports it ON while unset and
  // saves an OFF as sent. A plain switch, so not in IMMEDIATE_FLAGS or
  // CONFIRM_OFF_FLAGS: OFF only brings back the password screens 2.6 still
  // has.
  // Never signed out by a lost renewal (Issue 1, founder go 2026-10-21, "Go
  // on 1-6"; backend utils/refreshTokens). Server only, ON in production since
  // 11:43 AM 2026-10-21. Declared here and in the backend schema.
  {
    name: "auth_forgive_v1",
    label: "Keep Members Signed In When a Renewal Is Lost",
    help: "Every phone renews its sign-in in the background. When the answer to a renewal never reaches the phone (weak signal, the app closed half way), the phone later sends back its old key. Until now that signed the member out on every phone, often right as they tapped a notification. ON = that old key is forgiven once and the member stays signed in, only if the new key was never used and never cancelled (log out, password change, sign out everywhere), was issued within the last 30 days, and the account is not deleted, banned or suspended. Two renewals racing each other get 'Please try again.' instead of a sign-out. Each forgiveness is logged [AUTH-FORGIVE]. OFF = an old key signs the member out on every phone, as before, and the log counts what ON would have saved. Server only: every app version benefits, nothing to update.",
  },
  {
    name: "email_code_auth_v1",
    label: "Email Code Sign-In (2.6)",
    help: "On phones running 2.6 or newer, Create account and Sign in ask only for an email and send a 6-digit code to it. Typing the code creates the account or signs the member in. Sign in keeps a quiet 'Use my password' link, so members with a password and the App Review demo accounts still sign in with one. Closing the app never signs anyone out; a code is needed only on a new phone or after signing out. ON unless turned OFF here. OFF = 2.6 shows the password screens exactly as before and the server stops sending new codes (codes already sent still work). 2.5 and older never read this switch.",
  },
];

// Server-side flags. The app never reads these, so no release is involved at
// all — but until 2026-08-21 they had no switch here either and could only be
// changed by hand in the database.
const SERVER_FLAG_DEFS = [
  {
    name: "server_geocode_v1",
    label: "Server-Side Geocoding",
    help:
      "ON = the app asks the server to turn the member's coordinates into a city on the location screen. OFF = the phone's own geocoder, which is what every build before 2.3 does, so OFF is both the safe default and the kill switch if the location screen starts misbehaving. Unlike the rest of this group, the APP reads this one, but only v2.3 and newer; older binaries always use the on-device geocoder whatever this says.",
  },
  {
    name: "intro_inventory_gate",
    label: "Introduction Inventory Rules",
    help: "The matcher only creates a new introduction for members seen in the last 3 days, and never while someone already holds an introduction nobody has touched (one at a time since Sept 4). Turning this OFF returns to unlimited dealing, which is how 191 untouched pairs piled up in launch week.",
  },
  {
    name: "intent_pairing_v1",
    label: "Intent-Aware Pairing",
    help: "Members are tiered by what they have done: Proven (finished a Vault or chose Reveal), Untested (new), Ghost (served 2+ introductions, never answered). When ON, Proven and Club members are introduced to Proven members first, and a new member's first introduction is a Proven one whenever one fits. Since 30 September, someone who has never been introduced ranks with Proven members until their first introduction, except when a Club member or another new member is being introduced. Whether this is ON or OFF, a Ghost is never introduced to a Proven or Club member, nor one of them to a Ghost, even when nobody else is available. Tiers are recorded on every pair either way, so the loop funnel can split by pair type.",
  },
  {
    name: "reveal_hold_v1",
    label: "Hold the Next Introduction After a Reveal",
    help: "After a reveal, a member gets no new introduction while the revealed pair sits unwritten: their stage says the next one waits for this one. The hold lifts the moment they send a first message or close the connection on purpose, and at the latest at the next 6 PM deal, so a silent partner can never keep anyone out of the evening deal. A member who already has another live introduction keeps it. OFF returns to the ordinary wait after a reveal.",
  },
  // Kept, Together's emergency release (review 2026-10-02, backend
  // utils/keptTogether KEPT_RELEASE_FLAG). Declared here, in the backend
  // schema, in the server-only flag list and in KNOWN_FLAGS, or the next save
  // of this page turns it off.
  {
    name: "kept_release_pause_v1",
    label: "Kept, Together: Release Waiting Cards",
    help: "Emergency release, for a card that is failing members. ON = no Kept, Together card that is still waiting pauses anyone: both people keep writing after their 2 messages, the card stays offered, and it still opens the conversation once both answer. Takes effect on each phone's next chat refresh, within about two minutes. OFF = the card works as designed: after their 2 messages, each person answers before writing more, and someone who has answered replies once for each of the other person's messages. The Kept, Together switch cannot do this: OFF there stops new cards only. Server side; the app never reads this switch.",
  },
  {
    name: "meetup_free_unlock",
    label: "A Meetup Removes the Daily Message Limit",
    help: "ON is the old behaviour: tapping \"We have a plan\" lifts the daily message limit for that pair, free, on one tap by one person. OFF means declaring a meetup still records the plan and still tells the other member, but it buys no free chat for anyone, including pairs that already had it. Curiouzz Club becomes the only way past the daily limit. Turned OFF 2026-09-12 after a member tapped it 34 minutes into a conversation, with no plan and no venue ever opened, purely to escape the limit.",
  },
  {
    name: "meetup_proof_v1",
    label: "A Declared Meetup Must Actually Happen",
    help: "Confirming a meetup lifts the daily message limit for 48 hours instead of forever. After that both members are asked whether they met. Both say yes and the limit is gone for good, because meeting was always meant to be free. Either says no, or nobody answers within 24 hours, and the daily limit comes back with Curiouzz Club in front of it, and that pair cannot declare a meetup again. Also applies the message floor to the confirm itself, which until now only guarded the venue list. OFF returns to one tap, one side, permanent and unverified.",
  },
  {
    name: "end_friction_v1",
    label: "Ending Costs Time",
    help: "Curiouzz is not a swipe app. Only Ends made BEFORE opening the Vault count (an End after answering even one Vault question is a considered choice and costs nothing extra). The countdown after such an End grows with each one in the same day: the first is the normal 30 minutes, the second is 4 hours, the third and on arrive in 24 hours. A finished Vault or a reveal resets the count. Members with three or more pre-Vault Ends in a week and no reveal are introduced to others after everyone else, the way ghosts are. OFF returns to a flat 30-minute countdown after every End. Under One Introduction at a Time only the dealt-last ordering applies; the countdown ladder is replaced by the evening wait.",
  },
  {
    name: "own_outcome_evening_v1",
    label: "Your Own Outcomes Wait for the Evening",
    help: "After a reveal, or after closing a revealed connection yourself, nothing new arrives until the next 6 PM instead of about 30 minutes. An introduction you end before the reveal already waits for the evening whatever this switch says, unless you had answered all nine and were left waiting. Somebody else ending on you never costs you your day. This switch had lived only in code since September 16 and reset itself on every save; declared September 24.",
  },
  {
    name: "daily_cadence_v1",
    label: "One Introduction at a Time",
    help: "NOT once a day (renamed 2026-09-17: the old name survived a rework and was misleading). One introduction at a time for every member, and when one ends the next waits: about 30 minutes if the other person ended it or went quiet, or if you ended it after answering all nine while they had not; until 6 PM for any other introduction you end yourself (since September 24); and after a reveal, until you have said hello. 6 PM is also a delivery moment for everyone: unopened cards close and everyone whose wait is over is introduced. The End sheet names the wait before the tap. Reveal Together needs your own nine Vault answers. OFF keeps today's dealing, where the matcher hands one over as soon as it finds one; the 6 PM run then only writes a report.",
  },
  {
    name: "daily_cadence_test_v1",
    label: "One Introduction at a Time, Test Accounts Only",
    help: "The same one-at-a-time rules, for test accounts only, so the E2E accounts can be tested on real phones while every real member stays on today's dealing. App Review demo accounts are never included. Turn this OFF before any store submission. The toggle for everyone overrides this one.",
  },
  // Introductions never bother a member in a conversation (founder go
  // 2026-10-08, backend utils/quietIntro). Declared here, in the backend
  // schema and in the server-only flag list, or the next save of this page
  // turns it off.
  // A new introduction beside an open conversation (Issue 3/4, founder go
  // 2026-10-21; backend utils/openConversationIntro). Declared here and in
  // the backend schema. Its companion (below) must be ON for it to act.
  {
    name: "intro_open_conversation_v1",
    label: "New Introduction Beside an Open Conversation",
    help: "Works only while Introductions Wait Quietly During a Conversation is ON. ON = a member dealt a new card while they have an open revealed conversation that is not live gets it with no 'within a day' and no lapse: it closes with no blame if they have not opened it a day after they were told. Their push says 'Someone new to meet / Your conversation with {First} stays open. This is one more introduction, not a replacement.' Nobody is dealt less. The other person on that card: it closes a day after the member was told, even if they have answered (today the Promise reminds the member and keeps it a day longer), and they hear that it closed only when they are not dealt a new card that evening. Run the preview (api/scripts/openConversationPreview.js) before turning it ON. OFF = today's push and today's rules. Server only.",
  },
  {
    name: "quiet_intro_in_conversation_v1",
    label: "Introductions Wait Quietly During a Conversation",
    help: "A member in a live two-way conversation (an open revealed connection where both wrote in the last 24 hours) is still introduced and still introduced to others, exactly as now. What changes is how she is told: no push and no email about the new card, which waits on her Compatibility screen. The card has no 'within a day' deadline and no lapse for either person. The first time in an evening that she ends one without opening it, her next introduction is not held until 6 PM; a second such End before the next 6 PM run follows the ordinary rule, so she cannot be handed new introductions (each one a push to someone else) every half hour. If she has not opened it by the next 6 PM run (and it is over an hour old), it closes with no blame and both people join that same evening's introductions. The other person's push says 'Your Vault is waiting' and never names a deadline. Only cards dealt while this is ON are marked. Turning it OFF puts cards already dealt quietly back on the ordinary rules at once: she may then hear about them (for example 'X has started your Vault'), and they close by the ordinary 6 PM rules (the unopened-card expiry, or the Promise), still with no lapse for anyone. The member's conversation is read from the last message each side sent, so turn this ON at least a day after the backend that records it went live. Run the quiet introduction preview before turning it ON.",
  },
  // A 70% minimum for introductions, new members only (founder decisions
  // 2026-10-08, backend utils/newMemberMinimum). Declared here, in the backend
  // schema and in the server-only flag list, or the next save of this page
  // turns it off. Its minimum and start are NEW_MEMBER_MINIMUM_DEFS below.
  {
    name: "intro_min_score_new_members_v1",
    label: "A 70% Minimum for New Members",
    help: "Founder decisions, 8 October: new members only, and every city that is not Live counts as new. A new introduction where EITHER person is new must show at least the minimum on its card (70). New means an account created on or after the start below, or a member who was not in a Live city at the start (in a waitlist city, in a town no city covers yet, or with no city on file), who stays new after their city opens. If the best person for them shows less, the next best person at or above the minimum is introduced instead. If there is nobody, they are introduced to nobody that evening, and the 6 PM report counts them on a line of its own (belowNewMemberMinimum), never as 'nobody fitted'. An introduction always has two people, so a current member is never introduced to a new member under the minimum either. An introduction between two current members (signed up before the start and already in a Live city then) follows the same rule as today. A current member's evening can still change, though: a new member the minimum moves on from may be introduced to a current member someone else would have had, who is then introduced to someone else, or to nobody, that evening. A current member need not have been introduced: on 8 October, 42 of the 185 members in Ottawa had never been introduced (18 of them face verified), and their first introductions are not covered. All 180 active accounts outside a Live city that day count as new, and none of them can be introduced until their city opens or they move into a Live city. Introductions already made, their percentages and their Vaults are never changed. The number checked is the one the card shows: with the display floor at 60, 70 on the card is a raw compatibility score of 24%. What members read about who is near and who is free (the empty stage, the End sheet, the distance nudge) leaves out anyone the minimum would set aside, so nothing promises someone we would not introduce. In the 30 days to 8 October, 29 of the 581 introductions with someone on them who had signed up in the 30 days before showed under 70, about 1 in 20 (5 of them at 6 PM, 24 during the day). Most of its effect is during the day, which the preview cannot show: each 6 PM report counts the members a daytime introduction left with nobody because of it since the 6 PM before (newMemberMinimumDayNobody). Does nothing until a start is set. Turning it ON runs the preview, including what tonight would look like with the start 30 days earlier.",
  },
  // No wait after an introduction you never saw (founder 2026-10-09, backend
  // dailyCadence.endingPlanFor). Declared here, in the backend schema and in
  // the server-only flag list, or the next save of this page turns it off.
  {
    name: "unseen_close_no_wait_v1",
    label: "No Wait After an Introduction Never Seen",
    help: "When an introduction closes before someone ever saw it on their stage, by anything they did not do (the other person ended it, paused or deleted their account, or it closed on its own), they have nothing to wait for: they can be introduced again at once, at the next door, if someone is free. Before, they waited about 30 minutes and saw 'Your last introduction was closed. Nothing new arrives for about 30 minutes.' about an introduction they never saw. Never seen means no stage visit showed it and they gave no Vault answer to it; any stage visit counts as seen, however old. Their own Ends keep today's rules. The 6 PM run reads it: run the preview before turning it ON. Turn this ON first, then Tell One Person First. (Someone whose push Tell One Person First held and never sent already gets no wait, even while this is OFF.)",
  },
  {
    name: "vault_lockstep_v1",
    label: "Vault, One Tier at a Time",
    help: "A member can be at most one tier ahead of the other person: the Deeper Dive opens once the other has answered one question, the Wild Card once they have finished the Ice Breaker. Caps a one-sided Vault at three answers instead of nine. The partner gets a quiet push when someone is waiting on them. OFF = either side can finish all nine alone. Only 2.2 clients show the hold card.",
  },
  {
    name: "pair_profile_v1",
    label: "The Two of You (post-reveal profile)",
    help: "After a reveal, tapping the other person opens the new screen: their photos inside the ring, their name, Say hello, and the ring as the journey of the pair (Vault, Revealed, Talking, Met). OFF keeps the photo-first profile page. Only 2.2 clients read this.",
  },
  {
    name: "vault_voice_v1",
    label: "Say More (voice in the Vault)",
    help: "Optional twenty-second voice answers under each Deeper Dive question. Private to the pair: the other person hears it only after answering the same question, it is transcribed and moderated before it can be heard, and it is deleted when the introduction ends. Never part of the compatibility number. OFF hides the recorder everywhere; existing notes are cleaned up by the hourly sweep once their introduction ends.",
  },
  // Add a line (founder decisions 2026-10-08, backend utils/vaultLine).
  // Declared here, in the backend schema and in the public app-config flag
  // list, or the next save of this page turns it off.
  {
    name: "vault_line_v1",
    label: "Add a Line (typed, in the Vault)",
    help: "After picking an answer on an Ice Breaker or Wild Card question, a member can add one line of up to 80 characters saying why. Free for everyone. The other person sees that a line is waiting and reads it only after answering the same question. Checked by the same text check as chat before it can be read; refusals are recorded like chat refusals, and contact details refused or spotted across a member's lines light the admin bell. Every line, removed ones included, shows on the pair page under Vault Unlock. Never part of the compatibility number or the side by side. Offered only when the other person runs 2.5 or newer. Deleted when the introduction ends (hourly sweep). OFF hides it everywhere; existing lines are still deleted by the sweep.",
  },
  // Guess her answer / Guess the room (founder go 2026-10-19, backend
  // utils/guess). Declared here, in the backend schema and in the backend's
  // server-only flag list, or the next save of this page turns it off. Shown
  // under Introductions beside the reveal hold (founder 2026-10-20); the room
  // card is held (settingsMeta SECTIONS).
  {
    name: "guess_v1",
    label: "Guess her answer",
    help: "One Vault question a day while a member waits on a revealed, still-open connection: under the revealed card on their stage, a card asks them to guess that person's answer to a multiple choice Vault question the person already answered in their shared Vault, never the same question twice for one connection. The answer shows only after the guess, with the same 'agree on N of M' count the stage strip shows. It reads the Vault and never writes to it: nothing typed by anyone is ever shown, nothing here changes any pair, score or introduction, and nothing is a gesture, a hold or a vote. Test accounts can play. Only the 2.6 app and newer draw it; 2.5 and older never ask for it. OFF: the server answers that there is nothing today, so the card stops showing the next time the stage loads, and a guess on a card already on screen is refused.",
  },
  // The room card of the pair (founder 2026-10-19, "Do 1 only"): kept dark
  // because the quiet stage already draws the Daily Question's city split.
  // Declared here, in the backend schema and in the server-only flag list.
  {
    name: "guess_room_v1",
    label: "Guess the room (held)",
    help: "The second card of Guess her answer, for a quiet stage with nobody to wait on: how the member's city answered the same kind of Vault question, in counts only, shown once 20 real members answered it, with any bucket under 3 folded into Other, and never who. Held on 2026-10-19 because the quiet stage already shows the Daily Question's city split. Does nothing unless Guess her answer is ON, and the 2.6 build does not draw this card yet, so ON changes nothing on a phone today.",
  },
  {
    name: "photo_nudge_v1",
    label: "Photo Wall Nudge",
    help: "The hourly reminder to members who stopped at the photo step during signup. This switch is read on every tick, so turning it OFF stops the next reminder immediately.",
  },
  // The same reminder for Ottawa's early stoppers (founder 2026-10-01, "only
  // for users in the Ottawa pocket"). Declared here, in the backend schema
  // and in the server-only flag list, or the next save of this page turns it
  // off.
  {
    name: "photo_nudge_pre_habits_v1",
    label: "Photo Wall Nudge, Ottawa Members Who Stopped Earlier",
    help: "Adds members in the Ottawa pocket who stopped before the habits questions (no habits answer yet, their name and at least one more profile answer saved) to the reminder above: 'Your answers are saved' / 'Pick up right where you left off whenever you're ready. Your profile is waiting for you.' Same rules: 24 quiet hours first, two reminders for life, by email when the push cannot reach them. Does nothing while Photo Wall Nudge is OFF. The first hour after turning it ON reaches every Ottawa member who ever stopped there, however long ago, so ask for the dry-run list first.",
  },
  {
    name: "quiet_rotation_v1",
    label: "Quiet Rotation",
    help: "When a reveal offer is never opened and expires, quietly end the pair so the active member can be introduced to somebody new. Nobody is told a decision was made, because nobody made one. Intended to be turned ON when the doors open.",
  },
  {
    name: "reveal_thread_seed",
    label: "Reveal Seeds the Chat Thread",
    help: "When a reveal completes, the member whose selection completed it sends a wave into the conversation automatically, so the connection appears in Chat even if the celebration screen was never seen. Without this, a reveal that completes while the app is closed leaves the new connection findable nowhere.",
  },
  {
    name: "offapp_guard_v1",
    label: "Stop Usernames for Other Apps",
    help: "Checks every chat message a second after it is sent. When a member names another app (even spelled \"D Iscord\") or shares a username, email or link for one, the server asks a model to confirm on the recent conversation, then removes those messages for both people (they see \"This message was deleted\"), pushes the sender why, and alerts you here with the conversation. Nothing is removed on a pattern alone, and an ask on its own is never removed. Voice calls cannot be checked.",
  },
  // The server's message check (founder go 2026-09-29: "Go, and hold the
  // notification until it's checked"). Declared here, in the backend schema
  // and in the app config, or the next save of this page turns it off.
  {
    name: "chat_server_check_v1",
    label: "Check Every Message on the Server",
    help: "The server checks every new message between two members a second after it is sent, against the chat rules: contact details, explicit sexual messages aimed at the other person, anything sexual involving someone under 18, threats, slurs, and money scams. The other person's notification waits for the check, 5 seconds at most. A message that breaks a rule is removed for both people, the other person is never notified about it, the removal is recorded for review, and the In-Chat Line below says why when it is ON. A check slower than 5 seconds never costs anyone their notification (it never shows the message itself), and the message is still removed when the check answers. Edited messages are checked too. The 2.3 app keeps its own check; 2.4 skips its own only while this and the In-Chat Line below are both ON, and notices a change within a minute. OFF = today: the notification goes out at once and only the sender's phone checks the message.",
  },
  // Only the writer is told about a removal, and the 2.6 check before
  // sending (Issue 5, founder go 2026-10-21; backend docs/offapp-precheck.md).
  // Declared here, in the backend schema and in the app config.
  {
    name: "removal_writer_v1",
    label: "Only the Writer Hears About a Removed Message",
    help: "When Stop Usernames for Other Apps, or the server's message check for a contact detail, takes a message out. ON = the person who wrote it gets one push (at most one per half hour): 'Your message to {First}' / 'Curiouzz took it out because it had a username for another app. The rest of your chat is unchanged.' (or 'was about moving to another app.', or 'had contact details.'). A removal that shows none of these gets no push. The other person gets no push about it. With the In-Chat Line below ON, the line reads 'Curiouzz took out a message here. Curiouzz chats stay in Curiouzz.' and counts as unread for the writer, never for the other person. Removals for slurs, threats, scams or sexual messages keep today's words and send no push. OFF = today. Server only: every app version.",
  },
  {
    name: "offapp_precheck_v1",
    label: "Check Before Sending (2.6)",
    help: "ON = before a 2.6 phone sends a message that names another app, it asks the server whether Stop Usernames for Other Apps would take it out. If so, the message is never sent: only the writer sees why, the other person never sees or hears about it, and nothing is stored but a log row with no words. The rule itself is unchanged: holding a message never lets through what sending it would have taken out. Does nothing unless Stop Usernames for Other Apps is ON. OFF = the server answers 'send' and 2.6 sends as today. 2.5 and older never ask.",
  },
  {
    name: "offapp_notice_line",
    label: "In-Chat Line for Removed Messages",
    help: "Posts \"Curiouzz removed a message. Usernames and contact details for other apps can't be shared here.\" into the conversation when a message is removed. When the server's message check removes one, the line names the rule it broke instead. Shown centered to both members from 2.4. Turn it on once 2.4 is out: 2.2.1 and 2.3 show nothing for it, but it still adds one to the other person's unread count. 2.4 also reads it: while it is OFF, 2.4 keeps its own message check even with the server's check ON.",
  },
  // Who a live connection is (2026-09-26, backend utils/socketIdentity and
  // utils/socketGuard). Blocking is the fix for 2.3 phones; the alert switch
  // only watches; requiring the login comes once every phone runs 2.4.
  {
    name: "socket_guard_alert_v1",
    label: "Live Updates Guard, Alert Only",
    help: "Phones on 2.3 tell the server who they are with no proof, so someone who knows a member's id could receive that member's live updates (a reveal photo, Live coaster names and photos) and make them look in the app, which holds back their chat pushes. ON only watches and changes nothing for any member: when a live connection stays a member for over a minute from an internet address we have no record of that member using, an alert appears in the admin bell (the bell icon at the top of every page). Clicking it opens the member. With blocking also ON, the alert says the connection was held and received nothing. The first day after turning a guard switch ON can bring a few more alerts than usual, while the server learns which addresses members use. Measured before launch, a week of real traffic would have raised 1 alert.",
  },
  {
    name: "socket_guard_enforce_v1",
    label: "Live Updates Guard, Block",
    help: "The fix for phones on 2.3. When a live connection without a valid login says it is a member, the claim takes effect only once that member's own app has used the same public internet address, in the last 24 hours or while the connection is open; until then the connection receives nothing. This stops a stranger from taking a member's identity, unless the stranger shares the member's public internet address (the same home or office Wi-Fi, a venue's Wi-Fi, some mobile carriers). It also stops the brief claim 2.3 makes on the partner's id. Connections already open when this turns ON are checked too, within about 30 seconds. Phones on 2.4 prove themselves with their login and are not held, except a 2.4 phone whose login token has expired (for example, coming back from the background): it goes through the same check until its app refreshes the token. Measured on a week of real traffic: about 3 in 1,000 members' own connections never went live (all short, none missed an update) and about 2 in 100 waited up to 3 seconds. Turning it OFF takes effect after about a minute and a half: one failed settings read is never taken as OFF.",
  },
  {
    name: "socket_auth_required",
    label: "Require a Login for Live Updates",
    help: "Turn ON only once every phone runs 2.4 or later (raise the minimum app version first); older versions will lose live updates. ON refuses every live connection that does not prove who it is with the member's login, which only 2.4 and later send. Those phones keep working, but their screens only refresh on their own timers.",
  },
  {
    name: "count_gatekeeper_only",
    label: "Count Only Members Who Finished the Gatekeeper",
    help: "Decides who counts toward a city's doors-open gate, its progress meter and the place in line. ON = only members who finished the Gatekeeper and can actually be introduced to somebody. OFF = everyone who signed up. Turning this ON will visibly lower the member count members see, so it belongs after the nudge to people who stopped partway.",
  },
  // Possible duplicate accounts (founder decision 2026-10-13, backend
  // utils/dupeFlag). Declared here, in the backend schema and in the
  // server-only flag list, or the next save of this page turns it off.
  {
    name: "dupe_flag_v1",
    label: "Possible Duplicate Accounts",
    help: "Every hour, compares real accounts and lists pairs that may be one person under Members > Possible Duplicates, with the admin bell once for each pair. A pair is listed when the two have the same phone number; or the same birthday and first name, created up to 30 days apart; or used the same network in their first day and, created up to 7 days apart, have birthdays a day apart at most or the same first name, or the same gender and were created within an hour. The network counts only from the member's own activity (signing up, signing in, saving the profile, the face check), never from an admin's or a partner's, and never a busy network more than 3 of the accounts used (a phone carrier, a campus). When one joined through the other's invite, the pair says so. Test, deleted and staff accounts are never compared. A pair you mark Not the same person comes back to New, with one more bell, only if a later check finds something new about the two. Review only: it never blocks a sign-up or an introduction, never changes an account, and members see nothing. Internet addresses are compared without being kept. OFF = no scan runs; pairs already listed stay listed.",
  },
  // The face check must see eyes (founder 2026-10-14, after a member was
  // verified in sunglasses against photos in sunglasses; backend
  // utils/faceClear). Declared here, in the backend schema and in the
  // server-only flag list, or the next save of this page turns them off.
  {
    name: "face_check_eyes_visible_v1",
    label: "Face Check: Eyes Visible in the Selfie",
    help: "Before a face check can pass, the selfie must show the eyes. Amazon Rekognition looks at the selfie at the same time as the face comparison, so the wait does not grow. A selfie in sunglasses, or with a second face nearly as big as the member's, fails the check and the member is asked to take it again. It is a retry only: nothing changes on the account (a verified member stays verified) and it never counts against them. A covered face (a hand, a scarf, a mask) and a selfie where Rekognition sees no face are only logged, never failed. If Rekognition cannot answer within a few seconds, the check goes on as before. Nobody is left stuck: on 2.4 during sign-up there is no way back to the photos, so a member whose photos all hide the face, and whose selfie with the eyes showing has already failed, is let through in sunglasses and the admin bell rings for each one. What members read: 2.4 (every store app today) does not know this reason and shows its usual words, which say the selfie did not look enough like the photos and then that sunglasses stop it; the first sentence is not true here. 2.5 says it plainly. Measured on 14 October: 4 of 294 verified members' selfies are in sunglasses (2 of them got in only that way). Costs about $0.001 a check. OFF = the selfie is not looked at for this.",
  },
  {
    name: "face_check_clear_photo_v1",
    label: "Face Check: Pass Only Through a Clear Photo",
    help: "The face check can pass only through a profile photo that shows the face clearly: no sunglasses, not covered, and big enough (the biggest face at least 1% of the photo). In a group photo every face at least half as big as the biggest must be clear, so a friend's clear face does not count for the member. When no photo is clear, the check fails with 'Your photos need at least one clear photo of your face, without sunglasses. Change a photo, then try again.' During sign-up this is enforced only on 2.5 and newer with Change My Photos After a Failed Face Check ON, because 2.4 has no way back to the photos there and the member would be stuck. On 2.4 sign-ups (every store app today) the check runs as before, and a pass that came only through a photo that is not clear rings the admin bell for you to look. On the 'Please confirm it's you' check after a photo change it applies on any app, as a retry: the member stays verified and introductions stay paused until a check passes; they change a photo in Edit Profile. A photo Rekognition cannot read counts as clear. Measured on 14 October: 15 of 294 verified members have no clear photo (9 sunglasses, 3 covered, 2 too small, 1 no face). Costs about $0.001 a photo, once a day per photo. OFF = any one photo can give the pass, as before.",
  },
];

// The Curiouzz Promise (2026-09-01): the closure guarantee's two switches,
// in rollout order. The dry run watches; the live ladder acts.
const PROMISE_FLAG_DEFS = [
  {
    name: "answer_promise_dry_run",
    label: "Promise Dry Run",
    help: "A daily read-only sweep that reports who WOULD be prompted or closed under the Promise rules. Feeds the dry-run panel on the Analytics page. Contacts nobody, closes nothing.",
  },
  {
    name: "curiouzz_promise_v1",
    label: "The Curiouzz Promise, LIVE ladder",
    help: "The real thing, nightly at 6:00 PM: a member quiet for a day gets one honest prompt; still quiet a day later and the pair is closed kindly, both people told, both released back into rotation. Pairs quiet on both sides are released at day 2 too. Turning this OFF stops tonight's tick.",
  },
  // The Promise fixes (founder go 2026-10-08). Declared here, in the backend
  // schema and in the server-only flag list, or the next save of this page
  // turns them off.
  {
    name: "promise_prompt_unblocked_v1",
    label: "Promise Prompt Always Goes",
    help: "The one Promise prompt per introduction is no longer held back by another reminder sent in the last 20 hours, or by the member having unsubscribed from email (it is a push). A member away for 14 days is not pushed, but the prompt still counts, and so does one that cannot reach any phone or inbox, so the introduction closes the next evening as promised. Measured before: a median 52 hours to release, up to 120, and never for a member who had unsubscribed from email.",
  },
  {
    name: "promise_release_same_evening_v1",
    label: "Promise Release Joins Tonight's Introductions",
    help: "A member the Promise releases at 6 PM joins that same evening's introductions. Before, their 30-minute wait ended at 6:30 PM, after the evening run, so they waited until the next day.",
  },
  {
    name: "promise_close_quiet_side_push_v1",
    label: "Promise Close: Tell the Member Who Went Quiet",
    help: "When the Promise closes an introduction, the member who went quiet gets 'About one of your introductions / Your introduction with {First} has closed.' followed by their own true next step ('connection' after a reveal). Until now they were told nothing that reaches a phone. Wording awaits approval.",
  },
];

// Messaging and nudge switches that existed only in the database until
// 2026-09-01 (flipped by hand each time).
const NUDGE_FLAG_DEFS = [
  {
    name: "chat_message_push",
    label: "Chat Message Push",
    help: "Our own push for new chat messages (the CometChat dashboard extension stays OFF - both on at once means every message buzzes twice).",
  },
  // The chat quiet window (founder go 2026-09-29). Declared here, in the
  // backend schema and in the server-only flag list, or the next save of
  // this page turns it off.
  // A message never waits unseen, and the reply nudge after the reveal hellos
  // (Issue 2, founder go 2026-10-21; backend utils/unreadReminder and
  // utils/replyNudgeAfterHello). Declared here and in the backend schema.
  {
    name: "unread_reminder_v1",
    label: "Unread Message Reminder",
    help: "ON = when a real message (never a reveal hello, never a Curiouzz line) is still unread 2 hours after it was sent, the person it was sent to gets one push: '{First} wrote to you' / '{First}'s message is waiting in Chat.', or an email when no phone can be reached. Only 9 AM to 9 PM Eastern (held to 9 AM), never 5:45 to 6:15 PM, one per conversation and two per member a day, never on a break or with chat notifications off, every push checked by the truth check. OFF = nothing is read or sent. Server only: every app version.",
  },
  {
    name: "reply_nudge_after_hello_v1",
    label: "Reply Nudge Starts After a Real Message",
    help: "ON = the reveal hello no longer counts as writing for the reply nudge: the day of silence starts at the other person's first real message, the nudge goes only 10 AM to 8 PM Eastern, and it says 'Your conversation with {First}' / '{First}'s message is still waiting. Write back when you're ready.' Preview first (api/scripts/replyNudgePreview1021.js). OFF = the nudge exactly as before. Server only.",
  },
  {
    name: "chat_quiet_window_v1",
    label: "Chat Quiet Window",
    help: "After one chat notification about a conversation, the next ones from that conversation wait 10 minutes, then arrive as one: '<Name> sent you 3 messages'. OFF = one notification per message.",
  },
  // The closing notice (founder go 2026-09-29). Declared in the same three
  // places for the same reason.
  {
    name: "intro_closed_notice_v1",
    label: "Tell Both Members When an Unstarted Introduction Closes",
    help: "At 6 PM, an introduction closes when neither person started the Vault within a day. ON tells both of them. Someone given a new introduction that same evening hears it inside that one push ('Your last one closed at 6 PM because neither of you started the Vault within a day, so we've chosen someone new.'); everyone else gets 'Your introduction closed' with the reason and their own next step. Each notice is checked for truth before it goes, once per introduction. OFF = those introductions close in silence, as before.",
  },
  // The two notices that go with quiet introductions (founder go 2026-10-08).
  // Declared in the same three places for the same reason.
  {
    name: "quiet_intro_release_push_v1",
    label: "Quiet Introduction: Tell Her When Her Conversation Ends",
    help: "When the conversation of a member holding a quiet introduction ends (the connection closes, or one side has not written for 24 hours) and the card is still open and unopened, she gets the ordinary 'Your next introduction is ready' push, between 9 AM and 10 PM Toronto time only. The card then has a full day before it closes quietly. Does nothing while Introductions Wait Quietly During a Conversation is OFF.",
  },
  // A "ready" push must still be true when it is read (founder 2026-10-09,
  // "1. Yes, it must be true."; backend utils/introPushStagger,
  // dailyCadence.endingPlanFor, utils/stageNote, utils/introWithdraw).
  // Declared here, in the backend schema and in the server-only flag list,
  // or the next save of this page turns them off.
  {
    name: "intro_push_stagger_v1",
    label: "Tell One Person First",
    help: "Every new introduction used to push both people 'Your next introduction is ready' in the same second, and when the introduction then closed before the other one ever saw it, that push was no longer true (25 times in the 17 days to 9 October, 14 of them within 2 minutes, behind a one-star review). ON: the person whose stage opened most recently is told at once, and the other person's push waits 10 minutes. When someone is introduced while looking at their stage, or at their first introduction, only the other person is pushed, and that push waits. When the 10 minutes are up it goes only if the introduction is still open, they have not already seen it on their stage, and every other truth check still passes; seeing it first cancels it. 'Within a day to keep it' is said only if a full day is still left. At the 6 PM run that is never so: by 6:10 PM less than a full day is left before the next 6 PM, so the person told second at 6 PM hears 'Chosen with care. Your Vault is waiting.' without the deadline, while the person told first hears it. Never held: someone owed the 6 PM closing word (they are told first, and when both are owed one, both are told at once). Nobody waits when the person told first was already pushed in the last 10 minutes. Someone whose held push never went (the introduction closed in the 10 minutes, or a check stopped it) has no 30-minute wait after it. The introduction itself is never held: both people have it on their stage from the first second. The 7:30 AM truth report counts what was held and what came of it. OFF: both are pushed at once, as before. About 8 or 9 true pushes a day would arrive 10 minutes later.",
  },
  {
    name: "unseen_close_line_v1",
    label: "Say Why an Introduction Closed Before It Was Opened",
    help: "When we pushed someone 'Your next introduction is ready' (or, for a new member, 'Your first introduction is ready') and the other person ended it, paused or deleted their account before they opened it, their empty stage says 'That introduction closed before you could open it. It was nothing you did.' (when it closed within about an hour of the push) or 'That introduction closed before you opened it. It was nothing you did.' (later). No name. Shown on the first visit after it closed, only when there is no new introduction and no countdown on screen: under 'While you were away' on 2.4 and 2.5, in front of the stage line on 2.3. Never said after the 6 PM close or a Promise close. Wording approved 2026-10-09.",
  },
  {
    name: "intro_push_withdraw_v1",
    label: "Take a Stale Ready Notice Off 2.5 Phones",
    help: "When an introduction closes before someone saw it on their stage, and we had pushed them 'Your next introduction is ready' (or, for a new member, 'Your first introduction is ready') about it, a silent message takes that notice out of their phone's notifications. Only to a phone that itself runs 2.5 or newer: 2.3 and 2.4 would show it as 'New Message'. Each phone's own version is checked, so an older phone the same person still has signed in never gets it; a phone is known once it has opened the app after this server update. Not taken back when the same notice also said why their last introduction closed at 6 PM: that sentence is kept. Android removes it reliably; on iPhone it is best effort (iOS may delay or drop silent messages, and never wakes an app that was swiped away). Turn ON only once the 2.5 build that removes it is in both stores.",
  },
  {
    name: "quiet_card_close_notice_v1",
    label: "Quiet Introduction: Tell the Other Person When It Closes",
    help: "When a quiet introduction closes at 6 PM unopened, the person who was told about it (and was not given a new introduction that same evening) gets 'About one of your introductions / Your introduction with {First} has closed.' followed by their own true next step. Wording awaits approval. Does nothing while Introductions Wait Quietly During a Conversation is OFF.",
  },
  {
    name: "first_intro_notice",
    label: "First Introduction Notice",
    help: "Tells a member the moment their first introduction is ready, with an email fallback.",
  },
  {
    name: "next_intro_push",
    label: "Next Introduction Push",
    help: "The cooldown-ready push after an end: tells a member their next introduction is ready.",
  },
  {
    name: "vault_idle_reminder",
    label: "Vault Idle Reminder",
    help: "One quiet reminder when a member's unfinished Vault has sat untouched for a day, with an email fallback.",
  },
  {
    name: "truth_gate_v1",
    label: "Truth check on every push",
    help: "Every push is checked against the database at the moment it is sent, and one whose claim is no longer true is not sent (founder rule 2026-09-23: we cannot notify members of things that are not true). 'Waiting to reveal' and the Reignite notice wait a few minutes and are checked again when they go. Every decision is logged and summarised in the 7:30 AM truth report. OFF reverts to unchecked sends.",
  },
  {
    name: "vault_started_notice",
    label: "Vault Started Notice",
    help: "The instant invitation: the moment a member gives their FIRST answer in a shared Vault, the partner is told '{Name} has started your Vault. Join in and answer yours. You reveal together.' Once per starter per pair. The 24h idle reminder stays as the follow-up.",
  },
  {
    name: "reveal_waiting_notice",
    label: "Reveal Waiting Notice",
    help: "When one member selects Reveal Together but their partner has not, the partner gets 'Your connection is waiting to reveal.' Fixes the blind-reveal stall where one person commits and the other never gets a signal to come back. Once per pair; never names who selected.",
  },
  {
    name: "distance_widen_nudge",
    label: "Distance Widen Nudge",
    help: "Daily cron: finds members whose own distance setting is the only thing blocking a compatible, engaged introduction, and nudges them to widen it ('There are people just outside your range'). Once per member per 7 days. The empty-state prompt the whole category uses. Tap deep-links to the profile editor on v2.1 clients.",
  },
  {
    name: "re_pair_stale_v1",
    label: "Re-pair Stale Introductions",
    help: "Rescues members who have run out of fresh candidates in a small pool. Brings back an introduction that closed on its own more than 7 days ago ONLY when neither person ever saw it: it never reached either person's stage, nobody answered its Vault, it was never introduced again or reopened, and it was made after September 1. Once either person has seen an introduction, or anyone tapped End, those two are never introduced again (founder 2026-10-07). Fresh candidates always come first; a revived pair is a last resort when the alternative is an empty stage. Server-side, no app update needed.",
  },
  // The quiet stage (founder 2026-11-04, "Yes add both rows"): two server
  // switches that already reach members, shown here for the first time.
  {
    name: "age_curiosity_v1",
    label: "Age offer on the quiet stage",
    help: "When nobody new fits a member's settings, the empty stage shows \"2 people just outside your age range could be introduced to you.\" with an Adjust my range button. It counts only people at most 3 years outside the member's own range who would accept them back (their own settings include the member, the distance works both ways, face check and Gatekeeper done, not on a break, not blocked or hidden either way, the same city), and shows only from 2 people. It stays hidden for a member on a break, held by the face check, or who has not finished the Gatekeeper. The city's age limits are never named. Server side: every app version from 2.2 reads it. OFF: the offer never shows.",
  },
  {
    name: "stage_their_settings_line_v1",
    label: "\"Introductions go both ways\" line",
    help: "On the \"Nobody new near you can be introduced to you right now.\" stage, when 3 or more people near the member fit what they are looking for but are left out by their own settings (their gender wish, their ages, a distance they set; active in the last 30 days, not on a break, never introduced, not blocked or hidden either way), the Why it's quiet sheet opens with \"Introductions go both ways, so it also depends on who people near you are open to meeting.\" before the member's own settings. It names nobody and gives no count. 2.4 shows it under the headline, 2.5 and 2.6 as a row in the sheet, 2.3 and older never show it. The e2e test pair sees it while it is OFF. OFF: today's words.",
  },
  {
    name: "reveal_note_v1",
    label: "Reveal With a Note",
    help: "Lets the member whose choice completes a reveal attach a short note. Built and held: OFF until A Place to Start proves itself.",
  },
  {
    name: "reveal_hello_v1",
    label: "Reveal and Say Hello",
    help: "The Reveal tap asks for a one-line hello, prefilled from the pair's best shared Vault answer and fully editable, with no skip. Both hellos arrive as the first messages when both choose to reveal. Read only by 2.4 and newer; older apps are unaffected.",
  },
  {
    name: "pre_funnel_honesty",
    label: "Pre-Funnel Honesty",
    help: "The waitlist-deletion honesty messaging. Held for the version that renders it well.",
  },
];

// Chat ladder: the thresholds that decide what a pair may do next. Every one
// of these used to be a constant compiled into the app and the server, so
// moving a number meant a release. Order matters here: voice must stay below
// meetup, and the server refuses a save that inverts them.
const LADDER_DEFS = [
  {
    name: "voiceMinMessages",
    label: "Voice call unlocks after",
    help: "Messages in the conversation before a voice call becomes available. Hearing someone comes before agreeing to meet them.",
  },
  {
    name: "meetupMinMessages",
    label: "Pick a Spot unlocks after",
    help: "Messages before the pair can plan somewhere to meet. Must be higher than the voice call number.",
  },
  {
    name: "dailyMessageAllowance",
    label: "Messages per person per day",
    help: "Before a pair has confirmed a meetup. Confirming one removes the limit entirely, and so does Curiouzz Club.",
  },
  {
    name: "freeTextMessageLimit",
    label: "Meet or join the Club after",
    help: "Messages in ONE conversation, both people counted, before the pair has to choose: plan a meetup, or join Curiouzz Club. It does not reset. Must be higher than the Pick a Spot number, so a pair can always reach the meetup option before the chat asks them to decide. This field was labelled as a dead pre-1.9 value and the app had it switched off since 2026-09-07; it is the real gate again as of 2026-09-12.",
  },
];


// App config (founder ask 2026-09-16): the numbers that used to be compiled
// into the app, so changing one meant a build, an App Review and a wait.
// Bounds mirror api/utils/appConfigValues.js exactly — the server refuses
// anything outside them, and the app reverts anything outside them to the
// value it shipped with. Both ends are checked so a bad number cannot reach
// a phone, and so the operator is told rather than silently corrected.
const APP_CONFIG_DEFS = [
  {
    group: "Onboarding",
    name: "gatekeeperRequiredAnswers",
    label: "Gatekeeper answers required",
    help: "How many questions someone answers before onboarding counts as finished. These answers are their voice in an introduction, and the pocket count requires them, so this cannot go below 1.",
    min: 1,
    max: 5,
  },
  {
    group: "Onboarding",
    name: "tagsMinSelections",
    label: "Tags: minimum to continue",
    help: "The 'pick at least 5' floor on the interests, lifestyle, what-makes-me and see-the-world screens. Lower it if people are dropping out at those screens.",
    min: 1,
    max: 20,
  },
  {
    group: "Onboarding",
    name: "tagsMaxSelections",
    label: "Tags: maximum allowed",
    help: "The ceiling on the same four screens. Must be at least the minimum.",
    min: 1,
    max: 30,
  },
  {
    group: "Onboarding",
    name: "cityPreviewSkipMs",
    label: "City preview auto-skip (ms)",
    help: "How long the pre-signup city screen waits for its numbers before moving on by itself. It must never delay the funnel, so this is worth shortening if the server is slow.",
    min: 1000,
    max: 30000,
  },
  {
    group: "Preferences",
    name: "distanceMinKm",
    label: "Distance slider: minimum (km)",
    help: "The left end of the distance slider.",
    min: 1,
    max: 50,
  },
  {
    group: "Preferences",
    name: "distanceMaxKm",
    label: "Distance slider: maximum (km)",
    help: "The right end of the distance slider. This is the furthest anyone can ask to be introduced; the pocket's own pairing radius can tighten it further but never widen it. Must be above the minimum.",
    min: 5,
    max: 500,
  },
  {
    group: "Preferences",
    name: "ageMin",
    label: "Age slider: minimum",
    help: "The left end of the age range control. 18 is a legal floor and cannot be lowered from here.",
    min: 18,
    max: 30,
  },
  {
    group: "Preferences",
    name: "ageMax",
    label: "Age slider: maximum",
    help: "The right end of the age range control. Must be above the minimum.",
    min: 40,
    max: 120,
  },
  {
    group: "Chat and Vault",
    name: "voiceMessageMinMessages",
    label: "Voice messages unlock after",
    help: "Messages before a voice MESSAGE can be sent. Voice CALLS are set in the Chat Ladder above; this was hardcoded in the app until now, which is why the two halves of the same feature were tuned in different places.",
    min: 0,
    max: 100,
  },
  {
    group: "Chat and Vault",
    name: "sayMoreMaxMs",
    label: "Say More max length (ms)",
    help: "The ceiling on a spoken Vault answer. 20000 is 20 seconds.",
    min: 5000,
    max: 60000,
  },
  {
    group: "Reliability levers",
    name: "photoUploadTimeoutMs",
    label: "Photo upload timeout (ms)",
    help: "How long a photo upload waits before giving up. Raise it if people on slow connections are failing the photo wall; lower it if uploads are hanging.",
    min: 10000,
    max: 300000,
  },
  {
    group: "Reliability levers",
    name: "liveCoasterPollMs",
    label: "Live coaster poll (ms)",
    help: "How often the coaster screen asks the server for an update. Raise it to take load off the server. The floor is 5 seconds at both ends.",
    min: 5000,
    max: 120000,
  },
  {
    group: "Reliability levers",
    name: "liveRoomPollMs",
    label: "Live room poll (ms)",
    help: "The same, for the Live room screen.",
    min: 5000,
    max: 300000,
  },
];

// The new-member minimum's two settings (founder decisions 2026-10-08), shown
// right under its switch. Saved with the rest of the page; the server refuses
// a minimum that is not a whole percent or a start that is not a moment. The
// server records the cities Live with every new start it saves (the cities
// record, backend utils/newMemberMinimum; the stored start saved again, even
// cut to its minute, records nothing); the line under the start says whether
// it is recorded, because only then do members outside a Live city count as
// new.
const NEW_MEMBER_FLAG = "intro_min_score_new_members_v1";
const NEW_MEMBER_MINIMUM_DEFS = {
  min: {
    name: "introMinScoreNewMembers",
    label: "Minimum shown on the card",
    help: "The lowest percentage an introduction with a new member on it may show, as members see it on the card. 70 is the founder's number. A minimum at or below the display floor (60 today) changes nothing.",
    min: 1,
    max: 100,
  },
  since: {
    name: "introMinScoreNewMembersSince",
    label: "Start: who counts as new (Eastern time)",
    help: "Who counts as new: every account created on or after this moment, and every member who was not in a Live city at this moment (in a waitlist city, in a town no city covers yet, or with no city on file). They stay new after their city opens. A member who signed up before this moment and was already in a Live city then is a current member, even if never introduced. An introduction is covered when EITHER person is new, so a current member is never introduced to a new member under the minimum. An introduction between two current members is never held to it, though some current members may be introduced to someone else on a given evening. A current member who moves to a city that was not Live at the start counts as new there. A member who moves into a Live city after the start is new, and so is a member of a town added to a Live city after it. The Live cities are the ones Live when the start is saved; a city taken off Live later keeps its members current. Test accounts count by their sign-up date only. On 8 October, of the 365 active accounts, the 185 in Ottawa were current (42 of them never introduced, 18 of those face verified) and the other 180 count as new (91 in waitlist cities, 53 in towns no city covers, 36 with no city on file); none of the 180 can be introduced until their city opens or they move into a Live city. The part about cities is in force once the cities Live at the start are recorded: saving a new start here records them, and the start saved on 8 October is recorded once, by hand. Until then only the sign-up date counts. 'Now' fills in the present moment. An earlier start also covers everyone who signed up, or first joined a Live city, since then, who are current members today. Empty means the switch does nothing.",
  },
};

// The monthly cap on asking the Daily Question (founder 2026-11-04, "Yes to
// all 6, build them"), right under the Ask switch. It lives on the server
// beside the drop hour and the city card's threshold (adminSettings
// dailyQuestion.askMonthlyCap, default 2) and is applied at Send (backend
// utils/dailyQuestionAsk): questions asked in the Eastern calendar month,
// declined, AI-rejected and deleted ones not counted, test accounts capped
// too. 0 = no cap. The server refuses anything but a whole number from 0 to
// 31, and refuses the drop hour or the threshold changed from here.
const ASK_FLAG = "daily_question_ask_v1";
const ASK_CAP_DEF = {
  name: "dailyQuestion.askMonthlyCap",
  label: "Monthly cap on asking",
  help: "Questions a Club member may ask per month. 0 means no limit.",
  min: 0,
  max: 31,
};

// 🔴 KILL SWITCHES (truth_gate_v1, 2026-09-23). ON unless explicitly OFF,
// and saved the moment the operator saves, on their own, never held behind
// the flip preview: in an incident the switch must work at once. The backend
// stores one of them OFF only when the save names it in `confirmOff`, which
// is sent only for a switch the operator actually turned off here, so no
// other save can ever switch the truth check off by accident.
const IMMEDIATE_FLAGS = ["truth_gate_v1"];
const CONFIRM_OFF_FLAGS = ["truth_gate_v1"];

const AdminSettingLayer = () => {
  // Flip preview state (founder 2026-09-23). Declared here, above the
  // loading early return, because hooks must run on every render.
  const [previewFlagNames, setPreviewFlagNames] = useState([]);
  const [flipPreview, setFlipPreview] = useState(null); // { key, changes, job }
  useEffect(() => {
    getFlipPreviewFlags().then(setPreviewFlagNames).catch(() => setPreviewFlagNames([]));
  }, []);
  const [loading, setLoading] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState("");
  // Testing clock (2026-09-16): 0 = the real 6 PM. Above 0, TEST ACCOUNTS
  // get their "until the next 6 PM" wait that many minutes after the event
  // instead, so the evening rules can be walked without waiting. Real
  // members always get the real evening.
  const [testerDealMinutes, setTesterDealMinutes] = useState("0");
  // The new-member minimum (2026-10-08): strings while editing; the cutoff
  // is an Eastern "YYYY-MM-DDTHH:mm", or "" for none.
  const [newMemberMin, setNewMemberMin] = useState("70");
  const [newMemberSince, setNewMemberSince] = useState("");
  const [newMemberSaving, setNewMemberSaving] = useState(false);
  // The start as the server stores it, and the cities record it keeps with
  // it (2026-10-08): only while the record is for this very start do members
  // outside a Live city count as new.
  const [newMemberSinceStored, setNewMemberSinceStored] = useState(null);
  const [newMemberCities, setNewMemberCities] = useState(null);
  // The monthly cap on asking (2026-11-04): a string while editing. Known
  // only when the server reported it; an older backend cannot save it.
  const [askCap, setAskCap] = useState("");
  const [askCapKnown, setAskCapKnown] = useState(false);
  // App config (2026-09-16): the numbers that used to need a build. Held as
  // an object of strings while editing so a half-typed value does not become
  // NaN mid-keystroke; converted and validated on save.
  const [appConfig, setAppConfig] = useState(null);
  const [appConfigSaving, setAppConfigSaving] = useState(false);
  // Remote copy (2026-09-16). "from" must match the shipped English string
  // exactly; anything not listed renders as built, so an empty table is
  // today's app.
  const [copyRows, setCopyRows] = useState([]);
  const [copySaving, setCopySaving] = useState(false);
  const [ladder, setLadder] = useState(null);
  const [ladderSaving, setLadderSaving] = useState(false);
  const [flags, setFlags] = useState({});
  const [flagsSaving, setFlagsSaving] = useState(false);
  // Update levers (update-prompt spec 2026-08-26): the soft nudge versions
  // and the hard force-update gate, side by side so the operator always
  // sees both.
  const [versions, setVersions] = useState(null);
  const [versionsSaving, setVersionsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [engineSaving, setEngineSaving] = useState(false);
  // Drawer state. Sections that can reach live members open on a fresh
  // session; only the inert ones start closed, because a console whose
  // resting state shows no switches at all is slower during an incident than
  // the page it replaced. Each operator's own choice is remembered from there.
  const [openIds, setOpenIds] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("cz-settings-open") || "null");
      if (Array.isArray(saved)) return new Set(saved);
    } catch (e) { /* blocked or corrupt storage just means the defaults */ }
    return new Set(DEFAULT_OPEN);
  });
  const [helpOpen, setHelpOpen] = useState({});
  const [showAllHelp, setShowAllHelp] = useState(false);
  const [filter, setFilter] = useState("");
  const [baseline, setBaseline] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [saveResults, setSaveResults] = useState(null);

  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");

  // Integrations > Eventbrite (2026-10-19): read here, not in the card, so
  // the closed drawer's summary says what is connected.
  const [eventbrite, setEventbrite] = useEventbriteConnection(() => handleUnauthorized(navigate));

  // Only these two are read. canAdd/canView/canDelete/canReview were
  // destructured and never used; nothing on this page adds, deletes or
  // reviews anything.
  const { shouldCheckPermissions, canEdit } = getModulePermissions("admin-settings");

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // ✅ GET Compatibility Settings
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${BASE_URL}/admin/settings`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setRefreshInterval(
          response.data?.compatibility?.refreshInterval || ""
        );
        setTesterDealMinutes(
          String(response.data?.compatibility?.testerDealMinutes ?? 0)
        );
        setNewMemberMin(String(response.data?.compatibility?.introMinScoreNewMembers ?? 70));
        setNewMemberSince(etInputOf(response.data?.compatibility?.introMinScoreNewMembersSince));
        setNewMemberSinceStored(response.data?.compatibility?.introMinScoreNewMembersSince || null);
        setNewMemberCities(response.data?.compatibility?.introMinScoreNewMembersCities || null);
        const capNow = response.data?.dailyQuestion?.askMonthlyCap;
        setAskCapKnown(Number.isInteger(capNow));
        setAskCap(Number.isInteger(capNow) ? String(capNow) : "");
        setFlags(response.data?.flags || {});
        setAppConfig(response.data?.appConfig || null);
        setCopyRows(
          Array.isArray(response.data?.copyOverrides)
            ? response.data.copyOverrides
            : []
        );
        setLadder(response.data?.chatLadder || null);
        setVersions({
          latestAndroid: response.data?.latestAppVersion?.android || "",
          latestIos: response.data?.latestAppVersion?.ios || "",
          minSupported: response.data?.minSupportedAppVersion || "",
          rejectMissing: response.data?.rejectMissingAppVersion === true,
        });
      } catch (error) {
        if (error.response?.status === 401) {
          handleUnauthorized(navigate);
          return;
        }

        setErrorMessage("Error fetching settings");
        setTimeout(() => setErrorMessage(""), 3000);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // ✅ UPDATE Compatibility Settings
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!refreshInterval || refreshInterval < 60) {
      setErrorMessage("Refresh interval must be greater than 60 seconds");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    const testMinutes = Number(testerDealMinutes || 0);
    if (!Number.isInteger(testMinutes) || testMinutes < 0 || testMinutes > 1440) {
      setErrorMessage("Testing clock must be a whole number of minutes, 0 to 1440 (0 = off)");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    setEngineSaving(true);

    try {
      await axios.put(
        `${BASE_URL}/admin/settings`,
        { refreshInterval: Number(refreshInterval), testerDealMinutes: testMinutes },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      setSuccessMessage("Compatibility timer updated successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
      return { ok: true, label: "Engine and limits" };
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }

      setErrorMessage("Error updating settings");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setEngineSaving(false);
    }
  };

  // ✅ SAVE the new-member minimum (2026-10-08): the number and the cutoff,
  // together. The server checks both again and refuses, never clamps.
  const handleNewMemberMinimumSave = async () => {
    const min = Number(String(newMemberMin).trim());
    if (String(newMemberMin).trim() === "" || !Number.isInteger(min) || min < 1 || min > 100) {
      setErrorMessage(`"${NEW_MEMBER_MINIMUM_DEFS.min.label}" must be a whole number from 1 to 100`);
      setTimeout(() => setErrorMessage(""), 6000);
      return;
    }
    const since = newMemberSince ? instantOfEtInput(newMemberSince) : null;
    if (newMemberSince && !since) {
      setErrorMessage("The start must be a date and a time");
      setTimeout(() => setErrorMessage(""), 6000);
      return;
    }
    // The start goes only when it was edited: this page shows whole minutes,
    // so an untouched start sent back would move (23:41:00.718 to 23:41) and
    // the server would record the cities again for the moved start.
    const loadedSince = baseline ? baseline.newMember.slice(baseline.newMember.indexOf("|") + 1) : null;
    const sinceEdited = loadedSince === null || newMemberSince !== loadedSince;
    setNewMemberSaving(true);
    try {
      const res = await axios.put(
        `${BASE_URL}/admin/settings`,
        { introMinScoreNewMembers: min, ...(sinceEdited ? { introMinScoreNewMembersSince: since } : {}) },
        { headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" } }
      );
      const saved = res?.data?.data?.compatibility;
      if (saved) {
        setNewMemberSinceStored(saved.introMinScoreNewMembersSince || null);
        setNewMemberCities(saved.introMinScoreNewMembersCities || null);
      }
      setSuccessMessage("New-member minimum saved. Live within one minute.");
      setTimeout(() => setSuccessMessage(""), 4000);
      return { ok: true, label: "New-member minimum" };
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage(error.response?.data?.message || "New-member minimum not saved, nothing changed");
      setTimeout(() => setErrorMessage(""), 6000);
    } finally {
      setNewMemberSaving(false);
    }
  };

  // ✅ SAVE the monthly cap on asking (2026-11-04). Only the cap is sent:
  // the server refuses the drop hour or the threshold changed from here.
  // The Ask door reads it at every Send, so it applies to the next question.
  const handleAskCapSave = async () => {
    const raw = String(askCap).trim();
    const n = Number(raw);
    if (raw === "" || !Number.isInteger(n) || n < ASK_CAP_DEF.min || n > ASK_CAP_DEF.max) {
      setErrorMessage(`"${ASK_CAP_DEF.label}" must be a whole number from 0 to 31 (0 means no limit)`);
      setTimeout(() => setErrorMessage(""), 6000);
      return;
    }
    try {
      const res = await axios.put(
        `${BASE_URL}/admin/settings`,
        { dailyQuestion: { askMonthlyCap: n } },
        { headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" } }
      );
      const saved = res?.data?.data?.dailyQuestion?.askMonthlyCap;
      if (Number.isInteger(saved)) setAskCap(String(saved));
      setSuccessMessage("Monthly cap on asking saved. It applies to the next question a member sends.");
      setTimeout(() => setSuccessMessage(""), 4000);
      return { ok: true, label: "Monthly cap on asking" };
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage(error.response?.data?.message || "Monthly cap on asking not saved, nothing changed");
      setTimeout(() => setErrorMessage(""), 6000);
    }
  };

  // ✅ SAVE App Config
  const handleAppConfigSave = async () => {
    // Check the same bounds the server checks, so the operator gets a useful
    // message instead of a 400. The server is still the authority: it
    // re-validates and refuses anything that slips past here.
    const numeric = {};
    for (const def of APP_CONFIG_DEFS) {
      const raw = String(appConfig[def.name] ?? "").trim();
      const n = Number(raw);
      if (raw === "" || !Number.isInteger(n)) {
        setErrorMessage(`"${def.label}" must be a whole number`);
        setTimeout(() => setErrorMessage(""), 6000);
        return;
      }
      if (n < def.min || n > def.max) {
        setErrorMessage(
          `"${def.label}" must be between ${def.min} and ${def.max}`
        );
        setTimeout(() => setErrorMessage(""), 6000);
        return;
      }
      numeric[def.name] = n;
    }
    // Cross-field rules. A crossed pair would render a broken control, and
    // the app reverts BOTH ends rather than showing half a slider, so it is
    // better to refuse here than to let it silently revert there.
    if (numeric.distanceMaxKm <= numeric.distanceMinKm) {
      setErrorMessage("The distance maximum must be above the minimum");
      setTimeout(() => setErrorMessage(""), 6000);
      return;
    }
    if (numeric.ageMax <= numeric.ageMin) {
      setErrorMessage("The age maximum must be above the minimum");
      setTimeout(() => setErrorMessage(""), 6000);
      return;
    }
    if (numeric.tagsMaxSelections < numeric.tagsMinSelections) {
      setErrorMessage("The tag maximum must be at least the minimum");
      setTimeout(() => setErrorMessage(""), 6000);
      return;
    }

    setAppConfigSaving(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/admin/settings`,
        { appConfig: numeric },
        { headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" } }
      );
      setAppConfig(response.data?.data?.appConfig || numeric);
      setSuccessMessage(
        "App settings saved. Live on every phone within a minute, on any app version from 2.3 onward. Older versions keep the numbers they were built with."
      );
      setTimeout(() => setSuccessMessage(""), 8000);
      return { ok: true, label: "App settings" };
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage(
        error.response?.data?.message || "App settings not saved, nothing changed"
      );
      setTimeout(() => setErrorMessage(""), 6000);
    } finally {
      setAppConfigSaving(false);
    }
  };

  // ✅ SAVE Copy Overrides
  const handleCopySave = async () => {
    // Drop rows the operator started and abandoned rather than refusing the
    // whole save over an empty line at the bottom of the table.
    const rows = copyRows.filter(
      (r) => String(r.from || "").length > 0 || String(r.to || "").length > 0
    );
    for (const [i, r] of rows.entries()) {
      if (!String(r.from || "").trim() || !String(r.to || "").trim()) {
        setErrorMessage(
          `Row ${i + 1}: both the original and the replacement are needed`
        );
        setTimeout(() => setErrorMessage(""), 6000);
        return;
      }
      // The standing house rule, enforced server-side too.
      if (String(r.to).includes("\u2014")) {
        setErrorMessage(`Row ${i + 1}: em dashes are never used in Curiouzz copy`);
        setTimeout(() => setErrorMessage(""), 6000);
        return;
      }
    }
    const seen = new Set();
    for (const [i, r] of rows.entries()) {
      if (seen.has(r.from)) {
        setErrorMessage(`Row ${i + 1}: that original text is already overridden above`);
        setTimeout(() => setErrorMessage(""), 6000);
        return;
      }
      seen.add(r.from);
    }

    setCopySaving(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/admin/settings`,
        { copyOverrides: rows },
        { headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" } }
      );
      setCopyRows(response.data?.data?.copyOverrides || rows);
      setSuccessMessage(
        "Wording saved. It reaches phones on their next app open, on any app version from 2.3 onward."
      );
      setTimeout(() => setSuccessMessage(""), 8000);
      return { ok: true, label: "Wording" };
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage(
        error.response?.data?.message || "Wording not saved, nothing changed"
      );
      setTimeout(() => setErrorMessage(""), 6000);
    } finally {
      setCopySaving(false);
    }
  };

  // ✅ SAVE Feature Flags
  const handleLadderSave = async () => {
    const numeric = {};
    for (const def of LADDER_DEFS) {
      const n = Number(ladder?.[def.name]);
      if (!Number.isInteger(n) || n < 1) {
        setErrorMessage(`"${def.label}" must be a whole number above zero`);
        setTimeout(() => setErrorMessage(""), 4000);
        return;
      }
      numeric[def.name] = n;
    }
    // Mirrored client-side purely so the operator gets the reason immediately;
    // the server refuses it either way.
    if (numeric.freeTextMessageLimit <= numeric.meetupMinMessages) {
      setErrorMessage(
        "Meet or join the Club after must be higher than Pick a Spot unlocks after, " +
          "or a pair is asked to choose before they can plan a meetup."
      );
      return;
    }
    if (numeric.voiceMinMessages >= numeric.meetupMinMessages) {
      setErrorMessage(
        "The voice call number must be below the Pick a Spot number, otherwise a pair could be offered a meetup before they can hear each other"
      );
      setTimeout(() => setErrorMessage(""), 6000);
      return;
    }

    setLadderSaving(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/admin/settings`,
        { chatLadder: numeric },
        { headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" } }
      );
      setLadder(response.data?.data?.chatLadder || numeric);
      setSuccessMessage("Chat ladder saved. Live for everyone within one minute, on every app version.");
      setTimeout(() => setSuccessMessage(""), 6000);
      return { ok: true, label: "Chat ladder" };
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage(error.response?.data?.message || "Chat ladder not saved, nothing changed");
      setTimeout(() => setErrorMessage(""), 6000);
    } finally {
      setLadderSaving(false);
    }
  };

  const handleVersionsSave = async () => {
    const versionOk = (v) => v === "" || /^\d+(\.\d+)*$/.test(v.trim());
    for (const [label, v] of [
      ["Latest version (Android)", versions.latestAndroid],
      ["Latest version (iOS)", versions.latestIos],
      ["Minimum supported version", versions.minSupported],
    ]) {
      if (!versionOk(v)) {
        setErrorMessage(`"${label}" must look like 1.9 (or be empty to turn it off)`);
        setTimeout(() => setErrorMessage(""), 5000);
        return;
      }
    }
    setVersionsSaving(true);
    try {
      await axios.put(
        `${BASE_URL}/admin/settings`,
        {
          latestAppVersion: {
            android: versions.latestAndroid.trim(),
            ios: versions.latestIos.trim(),
          },
          minSupportedAppVersion: versions.minSupported.trim(),
          rejectMissingAppVersion: versions.rejectMissing,
        },
        { headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" } }
      );
      setSuccessMessage("App versions saved. Live within one minute.");
      setTimeout(() => setSuccessMessage(""), 4000);
      return { ok: true, label: "App versions" };
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage(error.response?.data?.message || "App versions not saved, nothing changed");
      setTimeout(() => setErrorMessage(""), 6000);
    } finally {
      setVersionsSaving(false);
    }
  };

  // Saves ONLY the switches named (the ones the operator changed). It used to
  // send every flag back, which rewrote switches nobody touched from a copy
  // that could be stale, and would have turned the truth check off had the
  // panel ever read it wrong.
  const handleFlagsSave = async (names) => {
    const changed = Object.fromEntries(names.map((n) => [n, flags[n] === true]));
    // Only a switch the operator set to exactly false: an unread (undefined)
    // value is never a deliberate OFF.
    const confirmOff = names.filter((n) => CONFIRM_OFF_FLAGS.includes(n) && flags[n] === false);
    setFlagsSaving(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/admin/settings`,
        confirmOff.length ? { flags: changed, confirmOff } : { flags: changed },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const notApplied = response?.data?.notApplied || [];
      if (notApplied.length) {
        const why = notApplied.map((n) => n.reason).join("; ");
        setErrorMessage(why);
        setTimeout(() => setErrorMessage(""), 6000);
        return { ok: false, label: "Feature flags", error: why };
      }
      setSuccessMessage("Feature flags saved. Live within one minute.");
      setTimeout(() => setSuccessMessage(""), 3000);
      return { ok: true, label: "Feature flags" };
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage("Error saving feature flags");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setFlagsSaving(false);
    }
  };

  // The values as loaded, so the save bar knows what is actually dirty.
  // Captured once, then reset after every successful save.
  useEffect(() => {
    if (baseline === null && !loading && ladder && appConfig && versions) {
      setBaseline({
        flags: JSON.parse(JSON.stringify(flags)),
        ladder: JSON.parse(JSON.stringify(ladder)),
        appConfig: JSON.parse(JSON.stringify(appConfig)),
        versions: JSON.parse(JSON.stringify(versions)),
        copyRows: JSON.stringify(copyRows),
        engine: String(refreshInterval) + "|" + String(testerDealMinutes),
        newMember: String(newMemberMin) + "|" + String(newMemberSince),
        askCap: String(askCap),
      });
    }
  }, [loading, ladder, appConfig, versions, flags, copyRows, refreshInterval, testerDealMinutes, newMemberMin, newMemberSince, askCap, baseline]);

  useEffect(() => {
    try {
      localStorage.setItem("cz-settings-open", JSON.stringify([...openIds]));
    } catch (e) { /* a blocked store only costs the preference */ }
  }, [openIds]);

  // Escape closes every open help panel, so the keyboard path out is the same
  // as the mouse path out.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setHelpOpen({});
        setShowAllHelp(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading) return <Loader />;

  const editable = !shouldCheckPermissions || canEdit;

  // Every flag definition, whichever array it was declared in. The arrays are
  // now only declarations; SECTIONS decides where a flag is SHOWN.
  const DEF_BY_NAME = {};
  [
    ...FLAG_DEFS,
    ...ENGAGEMENT_FLAG_DEFS,
    ...SERVER_FLAG_DEFS,
    ...PROMISE_FLAG_DEFS,
    ...NUDGE_FLAG_DEFS,
  ].forEach((d) => {
    DEF_BY_NAME[d.name] = d;
  });

  const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const dirtyFlagNames = baseline
    ? Object.keys(DEF_BY_NAME).filter(
        (k) => (baseline.flags[k] === true) !== (flags[k] === true)
      )
    : [];
  const ladderDirty = baseline ? !same(ladder, baseline.ladder) : false;
  const appConfigDirty = baseline ? !same(appConfig, baseline.appConfig) : false;
  const versionsDirty = baseline ? !same(versions, baseline.versions) : false;
  const copyDirty = baseline ? JSON.stringify(copyRows) !== baseline.copyRows : false;
  const engineDirty = baseline
    ? String(refreshInterval) + "|" + String(testerDealMinutes) !== baseline.engine
    : false;
  const newMemberDirty = baseline
    ? String(newMemberMin) + "|" + String(newMemberSince) !== baseline.newMember
    : false;
  const askCapDirty = baseline && askCapKnown ? String(askCap) !== baseline.askCap : false;

  // Which drawer each dirty thing belongs to, for the save bar's sentence.
  const sectionOfFlag = (name) => {
    const hit = SECTIONS.find(
      (sec) =>
        (sec.flags || []).includes(name) ||
        (sec.subGroups || []).some((g) => g.flags.includes(name))
    );
    return hit ? hit.title : "Settings";
  };
  const pendingMap = {};
  dirtyFlagNames.forEach((n) => {
    const t = sectionOfFlag(n);
    pendingMap[t] = (pendingMap[t] || 0) + 1;
  });
  if (ladderDirty) pendingMap["Chat, Vault and Reveal"] = (pendingMap["Chat, Vault and Reveal"] || 0) + 1;
  if (appConfigDirty) pendingMap["The app members are running"] = (pendingMap["The app members are running"] || 0) + 1;
  if (versionsDirty) pendingMap["The app members are running"] = (pendingMap["The app members are running"] || 0) + 1;
  if (copyDirty) pendingMap["The app members are running"] = (pendingMap["The app members are running"] || 0) + 1;
  if (askCapDirty) pendingMap["The app members are running"] = (pendingMap["The app members are running"] || 0) + 1;
  if (engineDirty) pendingMap["Engine and limits"] = (pendingMap["Engine and limits"] || 0) + 1;
  if (newMemberDirty) pendingMap["Introductions"] = (pendingMap["Introductions"] || 0) + 1;
  const pending = Object.entries(pendingMap).map(([label, count]) => ({ label, count }));
  const liveCount = dirtyFlagNames.filter((n) => REACHES_MEMBERS.has(n)).length;

  const dirtyCountFor = (sec) => {
    const names = [...(sec.flags || []), ...(sec.subGroups || []).flatMap((g) => g.flags)];
    let c = names.filter((n) => dirtyFlagNames.includes(n)).length;
    if (sec.id === "chat" && ladderDirty) c += 1;
    if (sec.id === "app" && (appConfigDirty || versionsDirty || copyDirty || askCapDirty)) {
      c += (appConfigDirty ? 1 : 0) + (versionsDirty ? 1 : 0) + (copyDirty ? 1 : 0) + (askCapDirty ? 1 : 0);
    }
    if (sec.id === "engine" && engineDirty) c += 1;
    if (sec.id === "introductions" && newMemberDirty) c += 1;
    return c;
  };

  /**
   * One save for the whole console.
   *
   * 🔴 SIX PUT KEYS CAN HALF SUCCEED. The flags can land while the ladder
   * 401s. So each key is reported separately and a mixed result never renders
   * as one green success; any section that failed is forced open and keeps its
   * changes, so nothing is silently lost.
   */
  // 🔴 THE FLIP RULE (founder 2026-09-23). A save that changes a switch which
  // alters how pairs behave first shows a dry run of tonight's 6 PM Promise
  // and deal, as things are and with the change (FlipPreviewPanel). The list
  // of such switches comes from the backend, so it cannot drift from the one
  // the preview itself accepts.
  // (its state and the switch list are declared at the top of the component,
  // above the loading return, as React requires of hooks)
  const pairFlagChanges = () =>
    dirtyFlagNames
      .filter((n) => previewFlagNames.includes(n))
      .map((n) => ({ name: n, value: flags[n] === true }));

  const handleSaveClick = async () => {
    // Kill switches first, alone and at once: never behind the preview.
    const immediate = dirtyFlagNames.filter((n) => IMMEDIATE_FLAGS.includes(n));
    let prior = [];
    if (immediate.length) {
      const r = (await handleFlagsSave(immediate)) || { ok: false, label: "Feature flags", error: "not saved" };
      prior = [{ ...r, label: immediate.map((n) => (DEF_BY_NAME[n] && DEF_BY_NAME[n].label) || n).join(", ") }];
      if (r.ok) {
        // Saved: no longer pending, whatever happens to the rest.
        setBaseline((b) =>
          b ? { ...b, flags: { ...b.flags, ...Object.fromEntries(immediate.map((n) => [n, flags[n] === true])) } } : b
        );
      }
      setSaveResults(prior);
      setTimeout(() => setSaveResults(null), r.ok ? 5000 : 12000);
      const restDirty =
        dirtyFlagNames.some((n) => !immediate.includes(n)) ||
        ladderDirty || appConfigDirty || copyDirty || versionsDirty || engineDirty || newMemberDirty || askCapDirty;
      if (!restDirty) return;
    }
    const changes = pairFlagChanges();
    if (!changes.length) return handleSaveAll({ skipFlags: immediate, prior });
    const key = JSON.stringify(changes);
    setFlipPreview({ key, changes, job: null });
    const overrides = Object.fromEntries(changes.map((c) => [c.name, c.value]));
    let job;
    try {
      job = await runFlipPreview(overrides);
    } catch (err) {
      job = { status: "failed", error: err?.response?.data?.message || err.message };
    }
    setFlipPreview((cur) => (cur && cur.key === key ? { ...cur, job } : cur));
  };

  // skipFlags: switches handleSaveClick has just saved on their own (this
  // render's dirty list still names them); prior: their result, so the
  // save bar reports everything the one click did.
  const handleSaveAll = async ({ skipFlags = [], prior = [] } = {}) => {
    setFlipPreview(null);
    setSavingAll(true);
    setSaveResults(null);
    const results = [...prior];
    const run = async (label, fn) => {
      const r = await fn();
      results.push(r || { ok: false, label, error: "not saved" });
    };
    try {
      // The new-member minimum's cutoff before the switches, so a switch
      // turned ON in the same save never runs on the cutoff it replaces.
      if (newMemberDirty) await run("New-member minimum", handleNewMemberMinimumSave);
      const flagNames = dirtyFlagNames.filter((n) => !skipFlags.includes(n));
      if (flagNames.length) await run("Feature flags", () => handleFlagsSave(flagNames));
      if (ladderDirty) await run("Chat ladder", handleLadderSave);
      if (appConfigDirty) await run("App settings", handleAppConfigSave);
      if (copyDirty) await run("Wording", handleCopySave);
      if (versionsDirty) await run("App versions", handleVersionsSave);
      if (engineDirty) await run("Engine and limits", () => handleSubmit({ preventDefault: () => {} }));
      if (askCapDirty) await run("Monthly cap on asking", handleAskCapSave);

      const failed = results.filter((r) => !r.ok);
      if (failed.length) {
        // Force every failed section open, so the operator lands on the
        // changes that did not save rather than on a banner.
        setOpenIds(new Set([...openIds, "chat", "app", "engine", "introductions", "messages"]));
      } else {
        setBaseline(null); // recaptured by the effect on the next render
      }
      setSaveResults(results);
      setTimeout(() => setSaveResults(null), failed.length ? 12000 : 5000);
    } finally {
      setSavingAll(false);
    }
  };

  const handleDiscard = () => {
    if (!baseline) return;
    setFlags(JSON.parse(JSON.stringify(baseline.flags)));
    setLadder(JSON.parse(JSON.stringify(baseline.ladder)));
    setAppConfig(JSON.parse(JSON.stringify(baseline.appConfig)));
    setVersions(JSON.parse(JSON.stringify(baseline.versions)));
    setCopyRows(JSON.parse(baseline.copyRows));
    const [ri, td] = baseline.engine.split("|");
    setRefreshInterval(ri);
    setTesterDealMinutes(td);
    const [nmMin, nmSince] = baseline.newMember.split("|");
    setNewMemberMin(nmMin);
    setNewMemberSince(nmSince);
    setAskCap(baseline.askCap);
  };

  const toggleSection = (id) => {
    const next = new Set(openIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpenIds(next);
  };

  // The filter has to reach text that is no longer on the page, because
  // browser find cannot see a closed drawer or a collapsed help panel.
  const q = filter.trim().toLowerCase();
  const matches = (def) =>
    !q ||
    def.label.toLowerCase().includes(q) ||
    def.name.toLowerCase().includes(q) ||
    String(def.help || "").toLowerCase().includes(q);

  const renderFlag = (name) => {
    const def = DEF_BY_NAME[name];
    if (!def || !matches(def)) return null;
    // A kill switch the server's settings do not name at all (this bundle is
    // ahead of its backend): showing it OFF would be untrue, and saving it
    // would be refused as an unknown flag. Deploy the backend first.
    if (IMMEDIATE_FLAGS.includes(name) && typeof flags[name] !== "boolean") {
      return (
        <div key={name}>
          <FlagRow
            def={def}
            value={undefined}
            dirty={undefined}
            editable={false}
            helpOpen={showAllHelp || helpOpen[name] === true}
            onToggleHelp={() => setHelpOpen({ ...helpOpen, [name]: !helpOpen[name] })}
            onChange={() => {}}
          />
          <div className="text-sm text-warning-600 mt-2 mb-8">
            The server did not report this switch, so its state is unknown here and it cannot be changed. If the
            backend with the truth check is not deployed yet, deploy it first.
          </div>
        </div>
      );
    }
    return (
      <FlagRow
        key={name}
        def={def}
        value={flags[name]}
        dirty={
          baseline && (baseline.flags[name] === true) !== (flags[name] === true)
            ? flags[name] === true
            : undefined
        }
        editable={editable}
        helpOpen={showAllHelp || helpOpen[name] === true}
        onToggleHelp={() => setHelpOpen({ ...helpOpen, [name]: !helpOpen[name] })}
        onChange={(checked) => setFlags({ ...flags, [name]: checked })}
      />
    );
  };

  // A number, its bounds as a suffix rather than a second stacked line, and
  // its help behind the same "i" as everything else. A bound is a constraint,
  // not prose.
  const NumberRow = ({ def, value, onChange, width = 110 }) => {
    if (!matches(def)) return null;
    const open = showAllHelp || helpOpen[def.name] === true;
    return (
      <div
        className="d-flex align-items-start justify-content-between gap-3 py-16"
        style={{ borderBottom: "1px solid var(--border-color)" }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="fw-semibold">{def.label}</span>
            <code className="text-xs text-secondary-light">{def.name}</code>
            <HelpButton
              name={def.name}
              help={def.help}
              open={open}
              onToggle={() => setHelpOpen({ ...helpOpen, [def.name]: !helpOpen[def.name] })}
            />
          </div>
          {open && (
            <div
              id={`help-panel-${def.name}`}
              className="mt-2 px-12 py-12 text-sm text-secondary-light radius-8"
              style={{ background: "rgba(140, 80, 253, 0.06)", lineHeight: 1.6 }}
            >
              {def.help}
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width, fontVariantNumeric: "tabular-nums", textAlign: "right" }}
            min={def.min}
            max={def.max}
            step={1}
            aria-label={def.label}
            value={value ?? ""}
            disabled={!editable}
            onChange={(e) => onChange(e.target.value)}
          />
          {def.min !== undefined && (
            <span className="text-xs text-secondary-light" style={{ whiteSpace: "nowrap" }}>
              {def.min} to {def.max}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Whether the cities Live at the start are recorded for the start in force
  // (founder 2026-10-08: "For every city that isn't Active, should be counted
  // as new"). Without the record only the sign-up date counts.
  const newMemberCitiesLine = () => {
    if (!newMemberSinceStored) return null;
    const rec = newMemberCities;
    const holds = !!(rec && rec.since && Date.parse(rec.since) === Date.parse(newMemberSinceStored));
    if (!holds) {
      return (
        <div className="text-sm text-warning-600 mt-2">
          The cities Live at this start are not recorded yet, so for now only the sign-up date counts. They are
          recorded whenever a new start is saved here.
        </div>
      );
    }
    const names = (rec.pockets || []).map((p) => p.name).filter(Boolean).join(", ") || "none";
    return (
      <div className="text-sm text-secondary-light mt-2">
        Cities Live when this start was recorded ({etInputOf(rec.takenAt).replace("T", " ")} ET): {names}. Members outside
        these cities, and members who joined one after the start, count as new.
      </div>
    );
  };

  // The new-member minimum's minimum and start, right under its switch
  // (2026-10-08). The start is typed in Eastern time and stored as a moment.
  const newMemberMinimumRows = () => {
    const sinceDef = NEW_MEMBER_MINIMUM_DEFS.since;
    const open = showAllHelp || helpOpen[sinceDef.name] === true;
    return (
      <div key="new-member-minimum" className="ps-24 mb-8">
        <NumberRow
          def={NEW_MEMBER_MINIMUM_DEFS.min}
          value={newMemberMin}
          width={90}
          onChange={(v) => setNewMemberMin(v)}
        />
        {matches(sinceDef) && (
          <div
            className="d-flex align-items-start justify-content-between gap-3 py-16"
            style={{ borderBottom: "1px solid var(--border-color)" }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="fw-semibold">{sinceDef.label}</span>
                <code className="text-xs text-secondary-light">{sinceDef.name}</code>
                <HelpButton
                  name={sinceDef.name}
                  help={sinceDef.help}
                  open={open}
                  onToggle={() => setHelpOpen({ ...helpOpen, [sinceDef.name]: !helpOpen[sinceDef.name] })}
                />
              </div>
              <div className="text-sm text-secondary-light mt-1">
                New members: accounts created on or after this moment, and members who were not in a Live city at
                this moment (they stay new after their city opens). An introduction is covered when either person is
                new. A member who signed up before this moment and was already in a Live city then is current, even if
                never introduced.
              </div>
              {open && (
                <div
                  id={`help-panel-${sinceDef.name}`}
                  className="mt-2 px-12 py-12 text-sm text-secondary-light radius-8"
                  style={{ background: "rgba(140, 80, 253, 0.06)", lineHeight: 1.6 }}
                >
                  {sinceDef.help}
                </div>
              )}
              {flags[NEW_MEMBER_FLAG] === true && !newMemberSince && (
                <div className="text-sm text-warning-600 mt-2">
                  No start is set, so the minimum covers nobody yet.
                </div>
              )}
              {newMemberCitiesLine()}
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <input
                type="datetime-local"
                className="form-control form-control-sm"
                style={{ width: 210 }}
                aria-label={sinceDef.label}
                value={newMemberSince}
                disabled={!editable}
                onChange={(e) => setNewMemberSince(e.target.value)}
              />
              {editable && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary radius-8"
                    onClick={() => setNewMemberSince(etInputOf(new Date()))}
                  >
                    Now
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger p-0"
                    onClick={() => setNewMemberSince("")}
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // The monthly cap on asking, right under the Ask switch (2026-11-04). A
  // server that did not report it cannot save it, so it is not offered.
  const askCapRows = () => {
    // The page's search box, as for every other setting.
    const found = !q || [ASK_CAP_DEF.label, ASK_CAP_DEF.name, ASK_CAP_DEF.help].some((t) => t.toLowerCase().includes(q));
    if (!found) return null;
    return (
      <div key="ask-monthly-cap" className="ps-24 mb-8">
        {askCapKnown ? (
          <NumberRow def={ASK_CAP_DEF} value={askCap} width={90} onChange={(v) => setAskCap(v)} />
        ) : (
          <div className="text-sm text-warning-600 py-16" style={{ borderBottom: "1px solid var(--border-color)" }}>
            The server did not report the monthly cap on asking, so it cannot be changed here. If the backend with the
            cap is not deployed yet, deploy it first.
          </div>
        )}
      </div>
    );
  };

  const appCfgGroup = (group) =>
    APP_CONFIG_DEFS.filter((d) => d.group === group).map((def) => (
      <NumberRow
        key={def.name}
        def={def}
        value={appConfig?.[def.name]}
        onChange={(v) => setAppConfig({ ...appConfig, [def.name]: v })}
      />
    ));

  const SubLabel = ({ children }) => (
    <p
      className="text-uppercase text-secondary-light mb-2 mt-3"
      style={{ fontSize: 11, letterSpacing: "0.06em" }}
    >
      {children}
    </p>
  );

  const sectionProps = (sec) => ({
    id: sec.id,
    icon: sec.icon,
    title: sec.title,
    summary: summarise(sec, flags, {
      pockets: null,
      ladder: ladder
        ? `voice at ${ladder.voiceMinMessages} · ${ladder.dailyMessageAllowance} a day`
        : null,
      minVersion: versions?.minSupported || "off",
      refreshInterval,
      testerDealMinutes,
      // From the live values: said only while the switch is ON.
      newMemberMinimum:
        flags[NEW_MEMBER_FLAG] === true
          ? newMemberSince
            ? `new members ${newMemberMin}% from ${newMemberSince.replace("T", " ")} ET`
            : "new-member minimum ON, no start"
          : null,
      integrations: integrationsSummary(eventbrite),
    }),
    open: openIds.has(sec.id),
    onToggle: () => toggleSection(sec.id),
    editable,
    dirtyCount: dirtyCountFor(sec),
  });

  const byId = (id) => SECTIONS.find((x) => x.id === id);

  return (
    <div className="row gy-4">
      <div className="col-lg-12">
        {successMessage && <SuccessAlert message={successMessage} />}
        {errorMessage && <DengerAlert message={errorMessage} />}

        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
          <div className="d-flex align-items-center gap-2">
            <Icon icon="ic:baseline-search" className="text-secondary-light" />
            <input
              type="search"
              className="form-control form-control-sm"
              style={{ width: 280 }}
              placeholder="Find a setting, or a flag key"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Find a setting"
            />
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="show-all-help"
                style={{ width: "3em", height: "1.5em", cursor: "pointer" }}
                checked={showAllHelp}
                onChange={(e) => setShowAllHelp(e.target.checked)}
              />
              <label className="form-check-label text-sm mb-0" htmlFor="show-all-help">
                Show all help
              </label>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary radius-8"
              onClick={() =>
                setOpenIds(
                  openIds.size === SECTIONS.length
                    ? new Set()
                    : new Set(SECTIONS.map((x) => x.id))
                )
              }
            >
              {openIds.size === SECTIONS.length ? "Collapse all" : "Open all"}
            </button>
          </div>
        </div>

        {/* Doors and Pockets */}
        <SettingsSection {...sectionProps(byId("doors"))}>
          {byId("doors").flags.map(renderFlag)}
          <div className="mt-3">
            <PocketsCard />
            <DoorsDashboardCard />
          </div>
        </SettingsSection>

        {/* Introductions */}
        <SettingsSection {...sectionProps(byId("introductions"))}>
          {byId("introductions").flags.map((name) =>
            name === NEW_MEMBER_FLAG ? (
              <React.Fragment key={name}>
                {renderFlag(name)}
                {newMemberMinimumRows()}
              </React.Fragment>
            ) : (
              renderFlag(name)
            )
          )}
        </SettingsSection>

        {/* Chat, Vault and Reveal */}
        <SettingsSection {...sectionProps(byId("chat"))}>
          {ladder && (
            <>
              <SubLabel>The chat ladder</SubLabel>
              {LADDER_DEFS.map((def) => (
                <NumberRow
                  key={def.name}
                  def={def}
                  value={ladder[def.name]}
                  width={90}
                  onChange={(v) => setLadder({ ...ladder, [def.name]: v })}
                />
              ))}
            </>
          )}
          {appConfig && (
            <>
              <SubLabel>Voice and the Vault</SubLabel>
              {appCfgGroup("Chat and Vault")}
            </>
          )}
          <SubLabel>Switches</SubLabel>
          {byId("chat").flags.map(renderFlag)}
        </SettingsSection>

        {/* Messages and the Promise */}
        <SettingsSection {...sectionProps(byId("messages"))}>
          {byId("messages").flags.map(renderFlag)}
          {(byId("messages").subGroups || []).map((g) => (
            <div key={g.label}>
              <SubLabel>{g.label}</SubLabel>
              {g.flags.map(renderFlag)}
            </div>
          ))}
        </SettingsSection>

        {/* The app members are running */}
        <SettingsSection {...sectionProps(byId("app"))}>
          {versions && (
            <>
              <SubLabel>Versions</SubLabel>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Latest version, Android</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. 1.9"
                    value={versions.latestAndroid}
                    disabled={!editable}
                    onChange={(e) => setVersions({ ...versions, latestAndroid: e.target.value })}
                  />
                  <small className="text-secondary-light">Set when the new version is live on Google Play.</small>
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Latest version, iOS</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. 1.9"
                    value={versions.latestIos}
                    disabled={!editable}
                    onChange={(e) => setVersions({ ...versions, latestIos: e.target.value })}
                  />
                  <small className="text-secondary-light">Set when the new version is live on the App Store.</small>
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Minimum supported version{" "}
                    <span className="bg-danger-100 text-danger-600 px-8 py-2 radius-4 text-xs fw-semibold">
                      Hard gate
                    </span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="empty = gate off"
                    value={versions.minSupported}
                    disabled={!editable}
                    onChange={(e) => setVersions({ ...versions, minSupported: e.target.value })}
                  />
                  <small className="text-secondary-light">
                    Anything older is locked out until it updates. Use only when an old version is
                    actively harmful.
                  </small>
                </div>
              </div>
              <div
                className="d-flex align-items-start justify-content-between gap-3 py-16"
                style={{ borderTop: "1px solid var(--border-color)" }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <span className="fw-semibold">Also lock out apps that do not declare a version</span>
                  <div className="text-sm text-secondary-light mt-1">
                    Locks out members who cannot be warned first
                  </div>
                </div>
                <div className="form-check form-switch flex-shrink-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    style={{ width: "3em", height: "1.5em", cursor: "pointer" }}
                    checked={versions.rejectMissing === true}
                    disabled={!editable}
                    aria-label="Also lock out apps that do not declare a version"
                    onChange={(e) => setVersions({ ...versions, rejectMissing: e.target.checked })}
                  />
                </div>
              </div>
            </>
          )}

          <SubLabel>Features in the app</SubLabel>
          {byId("app").flags.map((name) =>
            name === ASK_FLAG ? (
              <React.Fragment key={name}>
                {renderFlag(name)}
                {askCapRows()}
              </React.Fragment>
            ) : (
              renderFlag(name)
            )
          )}
          {(byId("app").subGroups || []).map((g) => (
            <div key={g.label}>
              <SubLabel>{g.label}</SubLabel>
              {g.flags.map(renderFlag)}
            </div>
          ))}

          {appConfig && (
            <>
              <SubLabel>Onboarding</SubLabel>
              {appCfgGroup("Onboarding")}
              <SubLabel>Preferences</SubLabel>
              {appCfgGroup("Preferences")}
            </>
          )}

          <SubLabel>Wording</SubLabel>
          <div className="text-sm text-secondary-light mb-2">
            Changes what every member reads, within a minute. Put the current wording on the left
            exactly as it appears on screen. Anything not listed stays as built.
          </div>
          {copyRows.length === 0 && (
            <p className="text-secondary-light text-sm mb-3">
              No wording overrides. The app reads exactly as it was built.
            </p>
          )}
          {copyRows.map((row, i) => (
            <div key={i} className="py-16" style={{ borderBottom: "1px solid var(--border-color)" }}>
              <div className="row g-2 align-items-start">
                <div className="col-md-5">
                  <label className="form-label mb-1 text-xs">Current wording (must match exactly)</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={row.from || ""}
                    disabled={!editable}
                    onChange={(e) => {
                      const next = [...copyRows];
                      next[i] = { ...next[i], from: e.target.value };
                      setCopyRows(next);
                    }}
                  />
                </div>
                <div className="col-md-5">
                  <label className="form-label mb-1 text-xs">New wording</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={row.to || ""}
                    disabled={!editable}
                    onChange={(e) => {
                      const next = [...copyRows];
                      next[i] = { ...next[i], to: e.target.value };
                      setCopyRows(next);
                    }}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label mb-1 text-xs">Note</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={row.note || ""}
                    placeholder="why"
                    disabled={!editable}
                    onChange={(e) => {
                      const next = [...copyRows];
                      next[i] = { ...next[i], note: e.target.value };
                      setCopyRows(next);
                    }}
                  />
                  {editable && (
                    <div className="d-flex align-items-center justify-content-between mt-2">
                      <div className="form-check mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`copy-on-${i}`}
                          checked={row.enabled !== false}
                          onChange={(e) => {
                            const next = [...copyRows];
                            next[i] = { ...next[i], enabled: e.target.checked };
                            setCopyRows(next);
                          }}
                        />
                        <label className="form-check-label text-xs" htmlFor={`copy-on-${i}`}>
                          On
                        </label>
                      </div>
                      <button
                        className="btn btn-sm btn-link text-danger p-0"
                        type="button"
                        onClick={() => setCopyRows(copyRows.filter((_, j) => j !== i))}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {editable && (
            <div className="mt-3">
              <button
                className="btn btn-outline-secondary btn-sm radius-8"
                type="button"
                onClick={() =>
                  setCopyRows([...copyRows, { from: "", to: "", note: "", enabled: true }])
                }
              >
                Add wording change
              </button>
            </div>
          )}
        </SettingsSection>

        {/* Engine and limits */}
        <SettingsSection {...sectionProps(byId("engine"))}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Refresh Interval (Seconds) <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="Enter refresh interval"
                value={refreshInterval}
                disabled={!editable}
                onChange={(e) => setRefreshInterval(e.target.value)}
                onBlur={(e) => {
                  // Clamp on BLUR, never per keystroke: clamping on change
                  // turned "120" into "60" the moment the "1" landed, which
                  // made the field impossible to type into.
                  const v = Number(e.target.value);
                  setRefreshInterval(!Number.isFinite(v) || v < 60 ? 60 : v);
                }}
                min="60"
              />
              <small className="text-secondary-light">
                The compatibility engine runs at this interval. Minimum 60 seconds.
              </small>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Testing Clock (Minutes)</label>
              <input
                type="number"
                className="form-control"
                placeholder="0"
                value={testerDealMinutes}
                disabled={!editable}
                onChange={(e) => setTesterDealMinutes(e.target.value)}
                min="0"
                max="1440"
              />
              <small className="text-secondary-light">
                Test accounts only. 0 means waits that run until the next 6 PM really wait for 6 PM.
                Real members always get the real evening.
              </small>
            </div>
          </div>
          {appConfig && (
            <>
              <SubLabel>Reliability levers</SubLabel>
              {appCfgGroup("Reliability levers")}
            </>
          )}
        </SettingsSection>

        {/* Integrations: outside services Curiouzz signs in to */}
        <SettingsSection {...sectionProps(byId("integrations"))}>
          <EventbriteCard
            state={eventbrite}
            onStatus={(status) => setEventbrite({ status })}
            editable={editable}
            onUnauthorized={() => handleUnauthorized(navigate)}
          />
        </SettingsSection>

        {/* Held and not shipped */}
        <SettingsSection {...sectionProps(byId("held"))}>
          {byId("held").flags.map(renderFlag)}
        </SettingsSection>
      </div>

      {editable && (
        <SaveBar
          pending={pending}
          liveCount={liveCount}
          saving={savingAll || flagsSaving || ladderSaving || appConfigSaving || copySaving || versionsSaving || engineSaving || newMemberSaving}
          results={saveResults}
          onSave={handleSaveClick}
          onDiscard={handleDiscard}
        />
      )}

      {editable && flipPreview && (
        <FlipPreviewPanel
          changes={flipPreview.changes}
          job={flipPreview.job}
          onSaveAnyway={() => handleSaveAll()}
          onCancel={() => setFlipPreview(null)}
        />
      )}
    </div>
  );
};

export default AdminSettingLayer;
